import { useState, useEffect, useRef, useCallback } from 'react';
import { Usd } from '@/utils/CurrencyContext';
import { useParams, useNavigate } from 'react-router-dom';
import { CourseIconPicker } from '@/components/CourseIconPicker';
import { ModelLetterEditor } from '@/components/teacher/ModelLetterEditor';
import { BrochureFileList } from '@/components/BrochureFileList';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabWithTooltip } from '@/components/ui/tab-with-tooltip';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Video, HelpCircle, Calendar, Upload, Trash2, ArrowLeft, FileText, Edit2, MessageCircle, Users, Mail, Phone, User, ClipboardList, CheckCircle, XCircle, Eye, Pencil, Loader2, Package, Music, X, ChevronUp, ChevronDown, GripVertical, RefreshCw, Download, ArrowUp, ArrowDown, MoveRight, FolderInput, Image, Search, RotateCcw, Award, Lock, Unlock, MessageSquare } from 'lucide-react';
import { uploadVideoToS3, extractFileKey, getPresignedDownloadUrl, isPublicAsset, getPublicUrl, uploadFileToS3 } from '@/utils/s3Upload';
import { uploadVideoToCloudflareStream, checkStreamStatus, updateLessonVideoUid, waitForVideoReady } from '@/utils/cloudflareStream';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableFileItem from '@/components/SortableFileItem';
import { ScrollArea } from '@/components/ui/scroll-area';

// Helper function to get URL for R2 files
const getR2MediaUrl = async (videoUrl, courseId) => {
  // Check if it's an R2 URL
  const isR2Url = videoUrl.includes('.r2.cloudflarestorage.com') || videoUrl.includes('.r2.dev');
  
  if (isR2Url) {
    const fileKey = extractFileKey(videoUrl);
    if (fileKey) {
      // Check if it's a public asset
      if (isPublicAsset(fileKey)) {
        return getPublicUrl(fileKey);
      }
      // Get presigned URL for private content
      try {
        const result = await getPresignedDownloadUrl(fileKey, courseId);
        return result.download_url;
      } catch (error) {
        console.error('Failed to get presigned URL:', error);
        return null;
      }
    }
  }
  
  // Legacy: Handle old /api/files/ URLs
  if (videoUrl.startsWith('/') || !videoUrl.includes('://')) {
    const token = localStorage.getItem('token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${videoUrl}?auth=${token}`;
  }
  
  return videoUrl;
};

// Media Preview Component - moved outside to prevent re-creation on render
const MediaPreview = ({ lesson, courseId }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(true);
  
  useEffect(() => {
    const loadUrl = async () => {
      if (!lesson?.video_url) {
        setIsLoadingUrl(false);
        return;
      }
      
      setIsLoadingUrl(true);
      const url = await getR2MediaUrl(lesson.video_url, courseId);
      setMediaUrl(url);
      setIsLoadingUrl(false);
    };
    
    loadUrl();
  }, [lesson?.video_url, courseId]);
  
  if (!lesson) return null;
  
  const { video_url, video_type, title } = lesson;

  if (video_type === 'youtube') {
    const videoId = new URL(video_url).searchParams.get('v') || video_url.split('/').pop();
    return (
      <div className="space-y-4" style={{ width: '100%' }}>
        <div 
          className="video-container mx-auto"
          style={{ 
            maxWidth: '1400px',
            height: '70vh',
            minHeight: '450px',
            maxHeight: '700px'
          }}
        >
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title={title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        <div className="text-center text-sm py-2 px-4 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
          <p>🎬 Enhanced View (2x Size) • Click full-screen button in player for maximum view</p>
        </div>
      </div>
    );
  }
  
  if (isLoadingUrl) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
        <span className="ml-2" style={{ color: '#5C635A' }}>Loading media...</span>
      </div>
    );
  }
  
  if (!mediaUrl) {
    return (
      <div className="text-center py-10" style={{ color: '#5C635A' }}>
        <p>Failed to load media. Please try refreshing.</p>
      </div>
    );
  }
  
  if (video_url.endsWith('.pdf')) {
    const iframeKey = `pdf-${lesson.id}-${Date.now()}`;
    
    return (
      <div className="space-y-6" style={{ width: '100%' }}>
        <div 
          style={{ 
            position: 'relative',
            width: '100%',
            height: 'calc(85vh - 40px)',
            minHeight: '700px',
            maxHeight: '1000px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <div 
            className="border-2 rounded-lg shadow-xl"
            style={{ 
              position: 'relative',
              width: '100%',
              maxWidth: '1200px',
              height: '100%',
              borderColor: '#E3E1DB',
              backgroundColor: '#fff',
              overflow: 'hidden'
            }}
          >
            <iframe
              key={iframeKey}
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(mediaUrl)}&embedded=true`}
              loading="eager"
              style={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={title}
            />
          </div>
        </div>
        
        <div className="text-center text-sm py-2 px-4 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
          <p>📄 Enhanced View (2x Size) • Full-screen button available in viewer toolbar</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 p-4 border rounded" style={{ borderColor: '#E3E1DB', backgroundColor: '#F0EFEA' }}>
          <div>
            <h4 className="font-semibold mb-3 text-center" style={{ color: '#163329' }}>View Options</h4>
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(mediaUrl, '_blank')}
                className="w-full"
              >
                📖 Open in New Tab (Full Zoom)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = mediaUrl;
                  link.download = title + '.pdf';
                  link.click();
                }}
                className="w-full"
              >
                ⬇️ Download PDF
              </Button>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-3 text-center" style={{ color: '#163329' }}>Print Options</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const printWindow = window.open(mediaUrl, '_blank');
                if (printWindow) {
                  printWindow.onload = () => {
                    printWindow.print();
                  };
                }
              }}
              className="w-full"
            >
              🖨️ Print PDF
            </Button>
            <p className="text-xs mt-2 text-center" style={{ color: '#5C635A' }}>
              Standard paper size (A4/Letter)
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-center gap-2 text-sm p-3 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
          <HelpCircle className="h-4 w-4" />
          <span>Scroll within the viewer • Use control buttons for more options</span>
        </div>
      </div>
    );
  } else {
    return (
      <div className="space-y-4">
        <div 
          className="mx-auto"
          style={{ 
            maxWidth: '1400px',
            minHeight: '450px'
          }}
        >
          <video 
            controls 
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture={false}
            preload="metadata"
            className="w-full rounded-lg"
            style={{ maxHeight: '700px', minHeight: '450px', backgroundColor: '#000' }}
          >
            <source src={mediaUrl} />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="text-center text-sm py-2 px-4 rounded" style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}>
          <p>🎬 Enhanced View (2x Size) • Click full-screen button for maximum view</p>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm" style={{ color: '#5C635A' }}>
          <div className="flex items-center gap-2">
            ✓ Play/Pause
          </div>
          <div className="flex items-center gap-2">
            ✓ Volume Control
          </div>
          <div className="flex items-center gap-2">
            ✓ Fullscreen Mode
          </div>
          <div className="flex items-center gap-2">
            ✓ Progress Seek
          </div>
        </div>
        <p className="text-xs text-center" style={{ color: '#5C635A' }}>
          Note: Downloading is disabled to protect content
        </p>
      </div>
    );
  }
};

const CourseManagePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [showAddSession, setShowAddSession] = useState(false);
  const [showEditCourse, setShowEditCourse] = useState(false);
  const [showEditLesson, setShowEditLesson] = useState(null);
  const [isEditingCourse, setIsEditingCourse] = useState(false);
  const [isEditingLesson, setIsEditingLesson] = useState(false);
  const [editCourseForm, setEditCourseForm] = useState({ 
    title: '', 
    description: '', 
    price: 0,
    brochure_text: '',
    brochure_file: null,
    brochure_files: []
  });
  const [editLessonForm, setEditLessonForm] = useState({ title: '', description: '', video_url: '', video_type: 'youtube', duration: '', is_purchasable: false, price: '' });
  const [newLessonForm, setNewLessonForm] = useState({ title: '', description: '', video_url: '', duration: '', is_purchasable: false, price: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  // Declaration state
  const [showDeclarationDialog, setShowDeclarationDialog] = useState(false);
  const [declarationContent, setDeclarationContent] = useState('');
  const [declarationStatus, setDeclarationStatus] = useState(null);
  const [isSavingDeclaration, setIsSavingDeclaration] = useState(false);
  const [isLoadingDeclaration, setIsLoadingDeclaration] = useState(false);
  const [isTogglingChat, setIsTogglingChat] = useState(false);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [lessonQuizzes, setLessonQuizzes] = useState({});
  const [showQuizDialog, setShowQuizDialog] = useState(null);
  const [quizForm, setQuizForm] = useState({
    title: '',
    questions: [{ question: '', options: ['', '', '', ''], correct_answer: 0 }]
  });
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  const [lessonVideoType, setLessonVideoType] = useState('youtube');
  const [editLessonVideoType, setEditLessonVideoType] = useState('youtube');
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [isAddingFile, setIsAddingFile] = useState(false);
  const [showAddFilesDialog, setShowAddFilesDialog] = useState(null); // lesson id for adding files
  const [showRenameDialog, setShowRenameDialog] = useState(null); // { lessonId, fileId, fileName }
  const [newFileName, setNewFileName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [replaceFileTarget, setReplaceFileTarget] = useState(null); // { lessonId, fileId }
  const [isReplacingFile, setIsReplacingFile] = useState(false);
  const replaceFileInputRef = useRef(null);
  const [replacePrimaryFileTarget, setReplacePrimaryFileTarget] = useState(null); // lessonId for primary file replacement
  const [isReplacingPrimaryFile, setIsReplacingPrimaryFile] = useState(false);
  const replacePrimaryFileInputRef = useRef(null);
  const [cloudflareStreamEnabled, setCloudflareStreamEnabled] = useState(false);
  const [pendingVideoUid, setPendingVideoUid] = useState(null); // Cloudflare Stream UID for new lesson
  const [videoProcessingStatus, setVideoProcessingStatus] = useState(null);
  
  // Brochure upload state
  const [isUploadingBrochure, setIsUploadingBrochure] = useState(false);
  const [brochureUploadProgress, setBrochureUploadProgress] = useState(0);
  const [replacingBrochureIndex, setReplacingBrochureIndex] = useState(null);
  const brochureFileInputRef = useRef(null);
  
  // Per-course gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(0);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryDescription, setGalleryDescription] = useState('');
  const galleryFileInputRef = useRef(null);
  
  // Certificate & Grading state
  const [studentGrades, setStudentGrades] = useState([]);
  const [isLoadingGrades, setIsLoadingGrades] = useState(false);
  const [certificateSetup, setCertificateSetup] = useState(null);
  const [isUploadingCertTemplate, setIsUploadingCertTemplate] = useState(false);
  const [certificateTerms, setCertificateTerms] = useState('');
  const [isSavingCertSetup, setIsSavingCertSetup] = useState(false);
  const [showGradeCommentDialog, setShowGradeCommentDialog] = useState(null); // student_id
  const [gradeComment, setGradeComment] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const certTemplateInputRef = useRef(null);
  
  // Enhanced upload progress state
  const [uploadStats, setUploadStats] = useState({
    bytesUploaded: 0,
    bytesTotal: 0,
    percentage: 0,
    estimatedSecondsRemaining: null,
    uploadStartTime: null,
    uploadRateSamples: [], // Rolling average of upload rates (bytes/sec)
    isProcessing: false, // True when upload is complete but video is still processing
    processingMessage: ''
  });
  
  // Additional file upload progress state keyed by lesson ID
  const [additionalFileUploadStats, setAdditionalFileUploadStats] = useState({});
  // Structure: { [lessonId]: { isUploading, percentage, bytesUploaded, bytesTotal, estimatedSecondsRemaining, rateSamples } }

  // Per-lesson file search queries (keyed by lesson ID)
  const [lessonFileSearchQueries, setLessonFileSearchQueries] = useState({});

  // Teacher per-device opened files tracking (localStorage only, no backend)
  const openedFilesStorageKey = `teacherOpenedFiles:${courseId}:${(localStorage.getItem('token') || 'anon').slice(-16)}`;
  const [teacherOpenedFiles, setTeacherOpenedFiles] = useState([]);

  // Hydrate teacherOpenedFiles from localStorage when courseId changes
  useEffect(() => {
    if (courseId) {
      try {
        const stored = localStorage.getItem(openedFilesStorageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setTeacherOpenedFiles(parsed);
          }
        }
      } catch (e) {
        console.warn('Failed to parse teacherOpenedFiles from localStorage:', e);
        setTeacherOpenedFiles([]);
      }
    }
  }, [courseId, openedFilesStorageKey]);

  // Mark a file as opened (adds to state and localStorage)
  const markFileOpened = (fileId) => {
    setTeacherOpenedFiles(prev => {
      if (prev.includes(fileId)) return prev;
      const updated = [...prev, fileId];
      try {
        localStorage.setItem(openedFilesStorageKey, JSON.stringify(updated));
      } catch (e) {
        console.warn('Failed to save teacherOpenedFiles to localStorage:', e);
      }
      return updated;
    });
  };

  // Clear all opened files tracking for this course
  const clearOpenedFiles = () => {
    setTeacherOpenedFiles([]);
    try {
      localStorage.removeItem(openedFilesStorageKey);
    } catch (e) {
      console.warn('Failed to clear teacherOpenedFiles from localStorage:', e);
    }
  };

  // Upload abort refs for cancel functionality
  const uploadAbortRef = useRef(null); // For main video upload
  const additionalFileAbortRef = useRef({}); // Keyed by lessonId for additional files

  // Cancel upload handler for main video
  const handleCancelMainUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current();
      uploadAbortRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadStats({
      bytesUploaded: 0,
      bytesTotal: 0,
      percentage: 0,
      estimatedSecondsRemaining: null,
      uploadStartTime: null,
      uploadRateSamples: [],
      isProcessing: false,
      processingMessage: ''
    });
    setPendingVideoUid(null);
    setVideoProcessingStatus(null);
    toast.info('Upload cancelled. No incomplete files were saved.');
  };

  // Cancel upload handler for additional files
  const handleCancelAdditionalFileUpload = (lessonId) => {
    if (additionalFileAbortRef.current[lessonId]) {
      additionalFileAbortRef.current[lessonId]();
      delete additionalFileAbortRef.current[lessonId];
    }
    setIsAddingFile(false);
    setAdditionalFileUploadStats(prev => {
      const newState = { ...prev };
      delete newState[lessonId];
      return newState;
    });
    toast.info('Upload cancelled. No incomplete files were saved.');
  };

  // Learning Kit state
  const [showKitSection, setShowKitSection] = useState(false);
  const [kitItems, setKitItems] = useState([]);
  const [isSavingKit, setIsSavingKit] = useState(false);
  const [kitLoaded, setKitLoaded] = useState(false);

  // Move lesson/file state
  const [showMoveLessonDialog, setShowMoveLessonDialog] = useState(null); // lesson object to move
  const [showMoveFileDialog, setShowMoveFileDialog] = useState(null); // { lessonId, file }
  const [teacherCourses, setTeacherCourses] = useState([]); // Other courses by this teacher
  const [isMoving, setIsMoving] = useState(false);
  const [selectedDestinationCourse, setSelectedDestinationCourse] = useState(null);
  const [selectedDestinationLesson, setSelectedDestinationLesson] = useState(null);
  const [destinationCourseLessons, setDestinationCourseLessons] = useState([]);

  // Calculate total kit price
  const totalKitPrice = kitItems.reduce((sum, item) => {
    return sum + ((item.quantity || 0) * (item.unit_price || 0));
  }, 0);

  useEffect(() => {
    fetchData();
    fetchKit();
    // Check if Cloudflare Stream is available
    checkStreamStatus().then(status => {
      setCloudflareStreamEnabled(status.configured);
    }).catch(() => {
      setCloudflareStreamEnabled(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchData = async () => {
    try {
      const [courseRes, detailRes, sessionsRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}`),
        api.get(`/live-sessions/course/${courseId}`),
      ]);
      
      setCourse(detailRes.data.course);
      setLessons(detailRes.data.lessons || []);
      setLiveSessions(sessionsRes.data);
      
      // Set edit course form values including brochure fields
      if (detailRes.data.course) {
        // Build brochure_files: use brochure_files if present, else wrap legacy single brochure_file, else empty array
        let brochureFiles = [];
        if (detailRes.data.course.brochure_files && detailRes.data.course.brochure_files.length > 0) {
          brochureFiles = detailRes.data.course.brochure_files;
        } else if (detailRes.data.course.brochure_file) {
          brochureFiles = [detailRes.data.course.brochure_file];
        }
        
        setEditCourseForm({
          title: detailRes.data.course.title || '',
          description: detailRes.data.course.description || '',
          price: detailRes.data.course.base_price || detailRes.data.course.price || 0,
          brochure_text: detailRes.data.course.brochure_text || '',
          brochure_file: detailRes.data.course.brochure_file || null,
          brochure_files: brochureFiles
        });
      }
      
      // Set terms content if exists
      if (detailRes.data.course?.terms_and_conditions) {
        setTermsContent(detailRes.data.course.terms_and_conditions);
      }
      
      // Fetch enrolled students
      fetchEnrolledStudents();
      
      // Fetch student grades and certificate setup
      fetchStudentGrades();
      fetchCertificateSetup();
      
      // Fetch gallery images
      fetchGalleryImages();
      
      // Fetch quizzes for all lessons
      if (detailRes.data.lessons?.length > 0) {
        fetchAllLessonQuizzes(detailRes.data.lessons);
      }
    } catch (error) {
      toast.error('Failed to load course data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await api.get(`/courses/${courseId}/gallery`);
      setGalleryImages(response.data || []);
    } catch (error) {
      console.error('Failed to load gallery images:', error);
      setGalleryImages([]);
    }
  };

  const fetchAllLessonQuizzes = async (lessonsList) => {
    const quizzesMap = {};
    for (const lesson of lessonsList) {
      try {
        const response = await api.get(`/lessons/${lesson.id}/quizzes`);
        quizzesMap[lesson.id] = response.data || [];
      } catch (error) {
        quizzesMap[lesson.id] = [];
      }
    }
    setLessonQuizzes(quizzesMap);
  };

  const fetchEnrolledStudents = async () => {
    setIsLoadingStudents(true);
    try {
      const response = await api.get(`/teacher/courses/${courseId}/students`);
      setEnrolledStudents(response.data.students || []);
    } catch (error) {
      console.error('Failed to load students');
    } finally {
      setIsLoadingStudents(false);
    }
  };

  // Certificate & Grading functions
  const fetchStudentGrades = async () => {
    setIsLoadingGrades(true);
    try {
      const response = await api.get(`/courses/${courseId}/enrolled-students-grades`);
      setStudentGrades(response.data.students || []);
      setCertificateSetup({
        is_complete: response.data.certificate_setup_complete
      });
    } catch (error) {
      console.error('Failed to load student grades');
    } finally {
      setIsLoadingGrades(false);
    }
  };

  const fetchCertificateSetup = async () => {
    try {
      const response = await api.get(`/courses/${courseId}/certificate-setup`);
      setCertificateSetup(response.data);
      setCertificateTerms(response.data.certificate_terms || '');
    } catch (error) {
      console.error('Failed to load certificate setup');
    }
  };

  const handleCertTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file');
      return;
    }
    
    setIsUploadingCertTemplate(true);
    try {
      const { promise } = uploadFileToS3(file, 'certificates/');
      const url = await promise;
      const fileKey = extractFileKey(url);
      
      await api.put(`/courses/${courseId}/certificate-setup`, {
        certificate_template_url: url,
        certificate_template_key: fileKey
      });
      
      toast.success('Certificate template uploaded');
      fetchCertificateSetup();
    } catch (error) {
      toast.error('Failed to upload template');
    } finally {
      setIsUploadingCertTemplate(false);
      if (certTemplateInputRef.current) {
        certTemplateInputRef.current.value = '';
      }
    }
  };

  const saveCertificateTerms = async () => {
    setIsSavingCertSetup(true);
    try {
      await api.put(`/courses/${courseId}/certificate-setup`, {
        certificate_terms: certificateTerms
      });
      toast.success('Certificate terms saved');
      fetchCertificateSetup();
    } catch (error) {
      toast.error('Failed to save certificate terms');
    } finally {
      setIsSavingCertSetup(false);
    }
  };

  const unlockCertificate = async (studentId) => {
    try {
      await api.post(`/courses/${courseId}/students/${studentId}/unlock-certificate`);
      toast.success('Certificate unlocked');
      fetchStudentGrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock certificate');
    }
  };

  const lockCertificate = async (studentId) => {
    try {
      await api.post(`/courses/${courseId}/students/${studentId}/lock-certificate`);
      toast.success('Certificate revoked');
      fetchStudentGrades();
    } catch (error) {
      toast.error('Failed to revoke certificate');
    }
  };

  const saveGradeComment = async () => {
    if (!showGradeCommentDialog) return;
    setIsSavingComment(true);
    try {
      await api.post(`/courses/${courseId}/students/${showGradeCommentDialog}/grade-comment`, {
        comment: gradeComment
      });
      toast.success('Comment saved');
      setShowGradeCommentDialog(null);
      setGradeComment('');
      fetchStudentGrades();
    } catch (error) {
      toast.error('Failed to save comment');
    } finally {
      setIsSavingComment(false);
    }
  };

  // Fetch kit items for the course
  const fetchKit = async () => {
    try {
      const response = await api.get(`/admin/courses/${courseId}/kit`);
      if (response.data?.items && response.data.items.length > 0) {
        setKitItems(response.data.items);
        setShowKitSection(true);
      } else {
        setKitItems([]);
      }
    } catch (error) {
      // Kit doesn't exist yet - that's ok
      setKitItems([]);
    } finally {
      setKitLoaded(true);
    }
  };

  // Kit item management functions
  const addKitItem = () => {
    setKitItems([...kitItems, { name: '', quantity: 1, description: '', unit_price: 0 }]);
  };

  const removeKitItem = (index) => {
    setKitItems(kitItems.filter((_, i) => i !== index));
  };

  const updateKitItem = (index, field, value) => {
    const updatedItems = [...kitItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setKitItems(updatedItems);
  };

  // Delete kit from backend
  const handleDeleteKit = async () => {
    if (!window.confirm('Remove the entire kit?')) return;
    try {
      await api.delete(`/admin/courses/${courseId}/kit`);
      setKitItems([]);
      setShowKitSection(false);
      toast.success('Kit removed');
      fetchData();
    } catch (e) {
      toast.error('Failed to remove kit');
    }
  };

  // Save kit to backend
  const handleSaveKit = async () => {
    // Validate kit items
    const validItems = kitItems.filter(item => item.name && item.name.trim());
    if (validItems.length === 0) {
      // If no valid items, delete the kit instead
      await handleDeleteKit();
      return;
    }

    setIsSavingKit(true);
    try {
      await api.post(`/admin/courses/${courseId}/kit`, {
        items: validItems,
        total_kit_price: totalKitPrice
      });
      toast.success('Learning kit saved successfully!');
      fetchData(); // Refresh course data to update pricing
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save kit');
    } finally {
      setIsSavingKit(false);
    }
  };

  // Fetch teacher's other courses for move functionality
  const fetchTeacherCourses = async () => {
    try {
      const response = await api.get('/teacher/courses');
      // Filter out the current course
      const otherCourses = (response.data || []).filter(c => c.id !== courseId);
      setTeacherCourses(otherCourses);
    } catch (error) {
      console.error('Failed to fetch teacher courses:', error);
      toast.error('Failed to load your courses');
    }
  };

  // Fetch lessons for a destination course (for file moves)
  const fetchDestinationCourseLessons = async (destCourseId) => {
    try {
      if (destCourseId === courseId) {
        // Use current lessons for the same course
        setDestinationCourseLessons(lessons || []);
      } else {
        const response = await api.get(`/courses/${destCourseId}/detail`);
        setDestinationCourseLessons(response.data.lessons || []);
      }
    } catch (error) {
      console.error('Failed to fetch destination course lessons:', error);
      setDestinationCourseLessons([]);
    }
  };

  // Handle opening move lesson dialog
  const openMoveLessonDialog = (lesson) => {
    setShowMoveLessonDialog(lesson);
    setSelectedDestinationCourse(null);
    fetchTeacherCourses();
  };

  // Handle opening move file dialog
  const openMoveFileDialog = (lessonId, file) => {
    setShowMoveFileDialog({ lessonId, file });
    setSelectedDestinationCourse(null);
    setSelectedDestinationLesson(null);
    setDestinationCourseLessons([]);
    fetchTeacherCourses();
  };

  // Handle destination course selection for file moves
  const handleDestinationCourseSelect = async (destCourseId) => {
    setSelectedDestinationCourse(destCourseId);
    setSelectedDestinationLesson(null);
    if (destCourseId) {
      await fetchDestinationCourseLessons(destCourseId);
    } else {
      setDestinationCourseLessons([]);
    }
  };

  // Move lesson to another course
  const handleMoveLessonToCourse = async () => {
    if (!showMoveLessonDialog || !selectedDestinationCourse) {
      toast.error('Please select a destination course');
      return;
    }

    setIsMoving(true);
    try {
      await api.patch(`/lessons/${showMoveLessonDialog.id}/move-to-course`, {
        destination_course_id: selectedDestinationCourse
      });
      toast.success('Lesson moved successfully!');
      setShowMoveLessonDialog(null);
      setSelectedDestinationCourse(null);
      fetchData(); // Refresh to show updated lessons
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move lesson');
    } finally {
      setIsMoving(false);
    }
  };

  // Move file to another lesson
  const handleMoveFile = async () => {
    if (!showMoveFileDialog || !selectedDestinationLesson) {
      toast.error('Please select a destination lesson');
      return;
    }

    setIsMoving(true);
    try {
      await api.patch(`/lessons/${showMoveFileDialog.lessonId}/files/${showMoveFileDialog.file.id}/move`, {
        destination_lesson_id: selectedDestinationLesson
      });
      toast.success('File moved successfully!');
      setShowMoveFileDialog(null);
      setSelectedDestinationCourse(null);
      setSelectedDestinationLesson(null);
      fetchData(); // Refresh to show updated files
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move file');
    } finally {
      setIsMoving(false);
    }
  };

  const handleEditCourse = async () => {
    if (!editCourseForm.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }
    
    setIsEditingCourse(true);
    try {
      await api.put(`/courses/${courseId}`, {
        title: editCourseForm.title,
        description: editCourseForm.description,
        price: parseFloat(editCourseForm.price) || 0,
        brochure_text: editCourseForm.brochure_text || null,
        brochure_file: editCourseForm.brochure_files?.[0] || editCourseForm.brochure_file || null,
        brochure_files: editCourseForm.brochure_files || []
      });
      toast.success('Course updated successfully!');
      setShowEditCourse(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course');
    } finally {
      setIsEditingCourse(false);
    }
  };

  // Upload a brochure PDF to R2 and return its file record
  const uploadBrochureFile = async (file) => {
    if (file.type !== 'application/pdf') throw new Error('Please upload a PDF file for the brochure');
    if (file.size > 20 * 1024 * 1024) throw new Error('Brochure file size must be less than 20MB');
    const { promise } = uploadFileToS3(file, 'courses/', setBrochureUploadProgress);
    const fileUrl = await promise;
    const fileKey = fileUrl.includes('.r2.dev/')
      ? fileUrl.split('.r2.dev/')[1]
      : fileUrl.includes('courses/')
        ? 'courses/' + fileUrl.split('courses/')[1].split('?')[0]
        : null;
    return { url: fileUrl, file_key: fileKey, filename: file.name, size_bytes: file.size };
  };

  // Handle brochure PDF upload
  const handleBrochureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if ((editCourseForm.brochure_files || []).length >= 5) {
      toast.error('Maximum of 5 brochure files allowed');
      return;
    }
    
    setIsUploadingBrochure(true);
    setBrochureUploadProgress(0);
    
    try {
      const newFile = await uploadBrochureFile(file);
      // Append to brochure_files array (not replace)
      setEditCourseForm(prev => ({
        ...prev,
        brochure_files: [...(prev.brochure_files || []), newFile],
        brochure_file: prev.brochure_files?.length === 0 ? newFile : prev.brochure_file // Keep legacy field for backward compat
      }));
      
      toast.success('Brochure uploaded successfully!');
    } catch (error) {
      console.error('Brochure upload error:', error);
      toast.error(error.message || 'Failed to upload brochure');
    } finally {
      setIsUploadingBrochure(false);
      setBrochureUploadProgress(0);
      if (brochureFileInputRef.current) {
        brochureFileInputRef.current.value = '';
      }
    }
  };

  // Replace a brochure file in place (keeps its position)
  const handleReplaceBrochure = async (index, file) => {
    setReplacingBrochureIndex(index);
    try {
      const newFile = await uploadBrochureFile(file);
      setEditCourseForm(prev => {
        const files = [...(prev.brochure_files || [])];
        files[index] = newFile;
        return { ...prev, brochure_files: files, brochure_file: files[0] || null };
      });
      toast.success('Brochure replaced. Click Save Changes to keep it.');
    } catch (error) {
      console.error('Brochure replace error:', error);
      toast.error(error.message || 'Failed to replace brochure');
    } finally {
      setReplacingBrochureIndex(null);
      setBrochureUploadProgress(0);
    }
  };

  // Remove brochure file by index
  const handleRemoveBrochure = (indexToRemove) => {
    setEditCourseForm(prev => {
      const newFiles = (prev.brochure_files || []).filter((_, idx) => idx !== indexToRemove);
      return {
        ...prev,
        brochure_files: newFiles,
        brochure_file: newFiles.length > 0 ? newFiles[0] : null // Update legacy field
      };
    });
  };

  // Handle gallery image upload
  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type - images only
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, WebP, or GIF)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image file size must be less than 10MB');
      return;
    }
    
    setIsUploadingGallery(true);
    setGalleryUploadProgress(0);
    
    try {
      const { promise, abort } = uploadFileToS3(file, 'gallery/', (percentage) => {
        setGalleryUploadProgress(percentage);
      });
      
      const fileUrl = await promise;
      
      // Extract file key from URL
      const fileKey = fileUrl.includes('.r2.dev/') 
        ? fileUrl.split('.r2.dev/')[1] 
        : fileUrl.includes('gallery/') 
          ? 'gallery/' + fileUrl.split('gallery/')[1]
          : null;
      
      // Add to gallery via API
      await api.post(`/courses/${courseId}/gallery`, {
        url: fileUrl,
        file_key: fileKey,
        title: galleryTitle || null,
        description: galleryDescription || null
      });
      
      toast.success('Image added to gallery!');
      setGalleryTitle('');
      setGalleryDescription('');
      fetchGalleryImages();
    } catch (error) {
      console.error('Gallery upload error:', error);
      toast.error('Failed to upload gallery image');
    } finally {
      setIsUploadingGallery(false);
      setGalleryUploadProgress(0);
      if (galleryFileInputRef.current) {
        galleryFileInputRef.current.value = '';
      }
    }
  };

  // Delete gallery image
  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await api.delete(`/courses/${courseId}/gallery/${imageId}`);
      toast.success('Image deleted!');
      fetchGalleryImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const openEditLessonDialog = (lesson) => {
    setEditLessonForm({
      title: lesson.title || '',
      description: lesson.description || '',
      video_url: lesson.video_url || '',
      video_type: lesson.video_type || 'youtube',
      duration: lesson.duration || '',
      is_purchasable: lesson.is_purchasable || false,
      price: lesson.price || ''
    });
    setEditLessonVideoType(lesson.video_type || 'youtube');
    setShowEditLesson(lesson.id);
  };

  const handleEditLesson = async () => {
    if (!editLessonForm.title.trim()) {
      toast.error('Please enter a lesson title');
      return;
    }
    if (!editLessonForm.video_url.trim()) {
      toast.error('Please enter a video URL or upload a file');
      return;
    }
    
    setIsEditingLesson(true);
    try {
      await api.put(`/lessons/${showEditLesson}`, {
        title: editLessonForm.title,
        description: editLessonForm.description,
        video_url: editLessonForm.video_url,
        video_type: editLessonVideoType,
        duration: parseInt(editLessonForm.duration) || null,
        is_purchasable: editLessonForm.is_purchasable || false,
        price: editLessonForm.is_purchasable ? (parseFloat(editLessonForm.price) || 0) : null
      });
      toast.success('Lesson updated successfully!');
      setShowEditLesson(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lesson');
    } finally {
      setIsEditingLesson(false);
    }
  };

  const handleEditLessonFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid video file (MP4, MOV, AVI, MPEG, WebM) or PDF');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('File size must be less than 5GB');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    // Initialize upload stats tracking
    let lastBytesUploaded = 0;
    let lastProgressTime = Date.now();
    let rateSamples = [];
    
    setUploadStats({
      bytesUploaded: 0,
      bytesTotal: file.size,
      percentage: 0,
      estimatedSecondsRemaining: null,
      uploadStartTime: Date.now(),
      uploadRateSamples: [],
      isProcessing: false,
      processingMessage: ''
    });
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
    toast.info(`Uploading ${file.size > 1024 * 1024 * 1024 ? fileSizeGB + 'GB' : fileSizeMB + 'MB'} file...`);
    
    try {
      // Upload directly to R2 using presigned URL with detailed progress tracking
      const { promise: uploadPromise, abort } = uploadVideoToS3(file, 'courses/', (progress, bytesUploaded, bytesTotal) => {
        // Use actual bytesUploaded and bytesTotal from XHR progress event
        const actualBytesUploaded = bytesUploaded || Math.floor((progress / 100) * file.size);
        const actualBytesTotal = bytesTotal || file.size;
        
        const now = Date.now();
        const timeDelta = (now - lastProgressTime) / 1000;
        const bytesDelta = actualBytesUploaded - lastBytesUploaded;
        
        if (timeDelta > 0 && bytesDelta > 0) {
          const rate = bytesDelta / timeDelta;
          rateSamples.push(rate);
          if (rateSamples.length > 10) rateSamples.shift();
        }
        
        lastBytesUploaded = actualBytesUploaded;
        lastProgressTime = now;
        
        const estimatedSeconds = calculateEstimatedTime(actualBytesUploaded, actualBytesTotal, rateSamples);
        
        setUploadProgress(progress);
        setUploadStats(prev => ({
          ...prev,
          bytesUploaded: actualBytesUploaded,
          bytesTotal: actualBytesTotal,
          percentage: progress,
          estimatedSecondsRemaining: estimatedSeconds,
          uploadRateSamples: [...rateSamples]
        }));
      });
      
      // Store abort function for cancel functionality
      uploadAbortRef.current = abort;
      
      const result = await uploadPromise;
      
      setEditLessonForm(prev => ({ ...prev, video_url: result.url }));
      setEditLessonVideoType('uploaded');
      toast.success('Upload complete!');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.message?.includes('aborted')) {
        // Don't show error for user-cancelled uploads
        return;
      }
      toast.error('File upload failed. Please try again');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStats({
        bytesUploaded: 0,
        bytesTotal: 0,
        percentage: 0,
        estimatedSecondsRemaining: null,
        uploadStartTime: null,
        uploadRateSamples: [],
        isProcessing: false,
        processingMessage: ''
      });
    }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Use state for video_url (which gets set by upload), fallback to form input for YouTube URLs
    const videoUrl = newLessonForm.video_url || formData.get('video_url');
    
    // Determine video type - use state if file was uploaded, otherwise use form selection
    let videoType = lessonVideoType;
    if (!videoUrl) {
      toast.error('Please enter a video URL or upload a file');
      return;
    }
    
    try {
      // Create the lesson first
      const response = await api.post(`/courses/${courseId}/lessons`, {
        title: formData.get('title') || newLessonForm.title,
        description: formData.get('description') || newLessonForm.description,
        video_url: videoUrl,
        video_type: videoType,
        duration: parseInt(formData.get('duration') || newLessonForm.duration) || null,
        cloudflare_video_uid: pendingVideoUid || null, // Include the Cloudflare Stream UID if available
        is_purchasable: newLessonForm.is_purchasable || false,
        price: newLessonForm.is_purchasable ? (parseFloat(newLessonForm.price) || 0) : null,
      });
      
      // If we have a pending Cloudflare Stream UID and got a lesson ID back, update the lesson
      if (pendingVideoUid && response.data?.lesson?.id) {
        try {
          await updateLessonVideoUid(response.data.lesson.id, pendingVideoUid);
        } catch (uidError) {
          console.warn('Failed to persist video UID:', uidError);
          // Non-fatal - the video_url already has the cloudflare:// URI
        }
      }
      
      toast.success('Lesson added successfully!');
      setShowAddLesson(false);
      setLessonVideoType('youtube'); // Reset for next lesson
      setNewLessonForm({ title: '', description: '', video_url: '', duration: '', is_purchasable: false, price: '' }); // Reset form
      setPendingVideoUid(null); // Clear pending video UID
      setVideoProcessingStatus(null);
      fetchData();
      e.target.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lesson');
    }
  };

  const handleSaveTerms = async () => {
    setIsSavingTerms(true);
    try {
      await api.put(`/courses/${courseId}/terms`, {
        terms_and_conditions: termsContent
      });
      toast.success('Course terms and conditions saved successfully!');
      setShowTermsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save terms and conditions');
    } finally {
      setIsSavingTerms(false);
    }
  };

  // Declaration handlers
  const fetchDeclaration = async () => {
    setIsLoadingDeclaration(true);
    try {
      const response = await api.get(`/courses/${courseId}/declaration`);
      if (response.data.declaration) {
        setDeclarationContent(response.data.declaration.text || '');
        setDeclarationStatus(response.data.declaration.status);
      } else {
        setDeclarationContent('');
        setDeclarationStatus(null);
      }
    } catch (error) {
      console.error('Error fetching declaration:', error);
    } finally {
      setIsLoadingDeclaration(false);
    }
  };

  const handleSaveDeclaration = async () => {
    if (!declarationContent.trim()) {
      toast.error('Declaration text is required');
      return;
    }
    setIsSavingDeclaration(true);
    try {
      // Check if declaration exists (has status) - if so, PUT to update, otherwise POST to create
      if (declarationStatus) {
        await api.put(`/courses/${courseId}/declaration`, { text: declarationContent });
        toast.success('Declaration updated and submitted for approval!');
      } else {
        await api.post(`/courses/${courseId}/declaration`, { text: declarationContent });
        toast.success('Declaration created and submitted for approval!');
      }
      setShowDeclarationDialog(false);
      fetchDeclaration();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save declaration');
    } finally {
      setIsSavingDeclaration(false);
    }
  };

  const openDeclarationDialog = () => {
    fetchDeclaration();
    setShowDeclarationDialog(true);
  };

  const handleToggleChat = async () => {
    setIsTogglingChat(true);
    try {
      const response = await api.put(`/courses/${courseId}/chat-settings`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to update chat settings');
    } finally {
      setIsTogglingChat(false);
    }
  };

  const [uploadProgress, setUploadProgress] = useState(0);

  // Helper to calculate rolling average upload rate
  const calculateEstimatedTime = (bytesUploaded, bytesTotal, rateSamples) => {
    if (rateSamples.length < 2) return null;
    
    // Use last 5 samples for rolling average
    const recentSamples = rateSamples.slice(-5);
    const avgRate = recentSamples.reduce((a, b) => a + b, 0) / recentSamples.length;
    
    if (avgRate <= 0) return null;
    
    const bytesRemaining = bytesTotal - bytesUploaded;
    const secondsRemaining = Math.ceil(bytesRemaining / avgRate);
    
    return secondsRemaining;
  };

  // Format seconds to human readable
  const formatTimeRemaining = (seconds) => {
    if (seconds === null || seconds === undefined) return '';
    if (seconds < 60) return `~${seconds}s remaining`;
    if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `~${mins}m ${secs}s remaining`;
    }
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `~${hours}h ${mins}m remaining`;
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Prevent multiple simultaneous uploads
    if (isUploading) {
      toast.warning('Please wait for the current upload to complete');
      return;
    }
    
    // Check file type
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm'];
    const validTypes = [...validVideoTypes, 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid video file (MP4, MOV, AVI, MPEG, WebM) or PDF');
      return;
    }
    
    // File size limit 5GB
    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('File size must be less than 5GB');
      return;
    }
    
    // Clear any previous upload path before starting new upload
    setNewLessonForm(prev => ({ ...prev, video_url: '' }));
    setPendingVideoUid(null);
    setVideoProcessingStatus(null);
    
    setIsUploading(true);
    setUploadProgress(0);
    
    // Initialize upload stats
    const startTime = Date.now();
    let lastBytesUploaded = 0;
    let lastProgressTime = startTime;
    let rateSamples = [];
    
    setUploadStats({
      bytesUploaded: 0,
      bytesTotal: file.size,
      percentage: 0,
      estimatedSecondsRemaining: null,
      uploadStartTime: startTime,
      uploadRateSamples: [],
      isProcessing: false,
      processingMessage: ''
    });
    
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
    toast.info(`Uploading ${file.size > 1024 * 1024 * 1024 ? fileSizeGB + 'GB' : fileSizeMB + 'MB'} file: ${file.name}`);
    
    try {
      // Use Cloudflare Stream for videos if available, R2 for PDFs or fallback
      const isVideoFile = validVideoTypes.includes(file.type);
      
      if (isVideoFile && cloudflareStreamEnabled) {
        // Upload to Cloudflare Stream with detailed progress tracking
        const { uid } = await uploadVideoToCloudflareStream(file, {
          onProgress: (percentage, bytesUploaded, bytesTotal) => {
            const now = Date.now();
            const timeDelta = (now - lastProgressTime) / 1000; // seconds
            const bytesDelta = bytesUploaded - lastBytesUploaded;
            
            // Calculate upload rate (bytes per second)
            if (timeDelta > 0 && bytesDelta > 0) {
              const rate = bytesDelta / timeDelta;
              rateSamples.push(rate);
              // Keep only last 10 samples
              if (rateSamples.length > 10) rateSamples.shift();
            }
            
            lastBytesUploaded = bytesUploaded;
            lastProgressTime = now;
            
            const estimatedSeconds = calculateEstimatedTime(bytesUploaded, bytesTotal, rateSamples);
            
            setUploadProgress(percentage);
            setUploadStats(prev => ({
              ...prev,
              bytesUploaded,
              bytesTotal,
              percentage,
              estimatedSecondsRemaining: estimatedSeconds,
              uploadRateSamples: [...rateSamples]
            }));
          },
          onSuccess: (videoUid) => {
            console.log('Video uploaded to Cloudflare Stream:', videoUid);
          },
          onError: (error) => {
            console.error('Cloudflare Stream upload error:', error);
          },
          onUploadCreated: (upload) => {
            // Store abort function for cancel functionality
            uploadAbortRef.current = () => upload.abort();
          }
        });
        
        // Store the UID for later use when creating the lesson
        setPendingVideoUid(uid);
        setNewLessonForm(prev => ({ ...prev, video_url: `cloudflare://${uid}` }));
        setLessonVideoType('cloudflare_stream');
        
        // Switch to processing state
        setUploadStats(prev => ({
          ...prev,
          percentage: 100,
          isProcessing: true,
          processingMessage: 'Processing video...'
        }));
        
        toast.info('Upload complete! Video is now being processed by Cloudflare...');
        setVideoProcessingStatus('processing');
        
        // Poll for video to be ready
        try {
          await waitForVideoReady(uid, {
            interval: 3000,
            timeout: 600000, // 10 minutes
            onStatusChange: (status) => {
              setVideoProcessingStatus(status.status);
              let message = 'Processing video...';
              if (status.status === 'queued') message = 'Queued for processing...';
              else if (status.status === 'inprogress') message = 'Transcoding video...';
              else if (status.status === 'ready') message = 'Video ready!';
              
              setUploadStats(prev => ({
                ...prev,
                processingMessage: message
              }));
              
              if (status.ready_to_stream) {
                toast.success('Video is ready for streaming!');
                setUploadStats(prev => ({
                  ...prev,
                  isProcessing: false,
                  processingMessage: 'Ready!'
                }));
              }
            }
          });
        } catch (err) {
          console.warn('Video processing status check failed:', err);
          // Don't fail the whole upload - video will still work, just might not be ready yet
          setUploadStats(prev => ({
            ...prev,
            isProcessing: false,
            processingMessage: 'Processing (check back later)'
          }));
        }
        
      } else {
        // Upload to R2 (for PDFs or when Cloudflare Stream is not available)
        const { promise: uploadPromise, abort } = uploadVideoToS3(file, 'courses/', (progress, bytesUploaded, bytesTotal) => {
          // Use actual bytesUploaded and bytesTotal from XHR progress event
          const actualBytesUploaded = bytesUploaded || Math.floor((progress / 100) * file.size);
          const actualBytesTotal = bytesTotal || file.size;
          
          const now = Date.now();
          const timeDelta = (now - lastProgressTime) / 1000;
          const bytesDelta = actualBytesUploaded - lastBytesUploaded;
          
          if (timeDelta > 0 && bytesDelta > 0) {
            const rate = bytesDelta / timeDelta;
            rateSamples.push(rate);
            if (rateSamples.length > 10) rateSamples.shift();
          }
          
          lastBytesUploaded = actualBytesUploaded;
          lastProgressTime = now;
          
          const estimatedSeconds = calculateEstimatedTime(actualBytesUploaded, actualBytesTotal, rateSamples);
          
          setUploadProgress(progress);
          setUploadStats(prev => ({
            ...prev,
            bytesUploaded: actualBytesUploaded,
            bytesTotal: actualBytesTotal,
            percentage: progress,
            estimatedSecondsRemaining: estimatedSeconds,
            uploadRateSamples: [...rateSamples]
          }));
        });
        
        // Store abort function for cancel functionality
        uploadAbortRef.current = abort;
        
        const result = await uploadPromise;
        
        // Set the video URL in the form using state
        setNewLessonForm(prev => ({ ...prev, video_url: result.url }));
        
        // Set the correct video type based on file extension
        setLessonVideoType('uploaded');
        
        toast.success(`Upload complete: ${file.name}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.message?.includes('timeout')) {
        toast.error('Upload timeout. Please try with a smaller file or better internet connection');
      } else if (error.message?.includes('Network')) {
        toast.error('Network error. Please check your internet connection');
      } else {
        toast.error(error.message || 'Video upload failed. Please try again');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset stats after a delay so user can see final state
      setTimeout(() => {
        if (!uploadStats.isProcessing) {
          setUploadStats({
            bytesUploaded: 0,
            bytesTotal: 0,
            percentage: 0,
            estimatedSecondsRemaining: null,
            uploadStartTime: null,
            uploadRateSamples: [],
            isProcessing: false,
            processingMessage: ''
          });
        }
      }, 3000);
    }
  };

  // Handle adding additional files to a lesson (supports multiple files)
  const handleAdditionalFileUpload = async (e, lessonId) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm', 'application/pdf', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac'];
    
    // Validate all files first
    const invalidFiles = [];
    const oversizedFiles = [];
    const validFiles = [];
    
    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        invalidFiles.push(file.name);
      } else if (file.size > 5 * 1024 * 1024 * 1024) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    }
    
    // Report validation errors
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type(s): ${invalidFiles.join(', ')}. Allowed: video, audio, PDF.`);
    }
    if (oversizedFiles.length > 0) {
      toast.error(`File(s) too large (>5GB): ${oversizedFiles.join(', ')}`);
    }
    
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }
    
    setIsAddingFile(true);
    const totalFiles = validFiles.length;
    let successCount = 0;
    let failCount = 0;
    
    // Upload files sequentially
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileIndex = i + 1;
      
      // Get file duration for video/audio files
      let fileDuration = null;
      const isMediaFile = file.type.startsWith('video/') || file.type.startsWith('audio/');
      
      if (isMediaFile) {
        try {
          fileDuration = await new Promise((resolve, reject) => {
            const mediaElement = file.type.startsWith('video/') 
              ? document.createElement('video')
              : document.createElement('audio');
            mediaElement.preload = 'metadata';
            mediaElement.onloadedmetadata = () => {
              URL.revokeObjectURL(mediaElement.src);
              resolve(mediaElement.duration);
            };
            mediaElement.onerror = () => {
              URL.revokeObjectURL(mediaElement.src);
              resolve(null);
            };
            mediaElement.src = URL.createObjectURL(file);
          });
        } catch (err) {
          console.warn('Could not get media duration:', err);
        }
      }
      
      // Initialize upload state for this lesson with multi-file info
      let lastBytesUploaded = 0;
      let lastProgressTime = Date.now();
      let rateSamples = [];
      
      setAdditionalFileUploadStats(prev => ({
        ...prev,
        [lessonId]: {
          isUploading: true,
          percentage: 0,
          bytesUploaded: 0,
          bytesTotal: file.size,
          estimatedSecondsRemaining: null,
          rateSamples: [],
          currentFileIndex: fileIndex,
          totalFiles: totalFiles,
          currentFileName: file.name
        }
      }));
      
      try {
        // Upload directly to R2 using presigned URL with progress tracking
        const { promise: uploadPromise, abort } = uploadVideoToS3(file, 'courses/', (progress, bytesUploaded, bytesTotal) => {
          const actualBytesUploaded = bytesUploaded || Math.floor((progress / 100) * file.size);
          const actualBytesTotal = bytesTotal || file.size;
          
          const now = Date.now();
          const timeDelta = (now - lastProgressTime) / 1000;
          const bytesDelta = actualBytesUploaded - lastBytesUploaded;
          
          if (timeDelta > 0 && bytesDelta > 0) {
            const rate = bytesDelta / timeDelta;
            rateSamples.push(rate);
            if (rateSamples.length > 10) rateSamples.shift();
          }
          
          lastBytesUploaded = actualBytesUploaded;
          lastProgressTime = now;
          
          const estimatedSeconds = calculateEstimatedTime(actualBytesUploaded, actualBytesTotal, rateSamples);
          
          setAdditionalFileUploadStats(prev => ({
            ...prev,
            [lessonId]: {
              isUploading: true,
              percentage: progress,
              bytesUploaded: actualBytesUploaded,
              bytesTotal: actualBytesTotal,
              estimatedSecondsRemaining: estimatedSeconds,
              rateSamples: [...rateSamples],
              currentFileIndex: fileIndex,
              totalFiles: totalFiles,
              currentFileName: file.name
            }
          }));
        });
        
        // Store abort function for cancel functionality
        additionalFileAbortRef.current[lessonId] = abort;
        
        const uploadResult = await uploadPromise;
        
        // Determine file type
        let fileType = 'video';
        if (file.type === 'application/pdf') {
          fileType = 'pdf';
        } else if (file.type.startsWith('audio/')) {
          fileType = 'audio';
        }
        
        // Then add file to lesson
        await api.post(`/lessons/${lessonId}/files`, {
          url: uploadResult.url,
          file_type: fileType,
          file_name: file.name,
          file_size: file.size,
          duration: fileDuration
        });
        
        successCount++;
      } catch (error) {
        failCount++;
        toast.error(`Failed to upload: ${file.name}`);
        // Continue with next file
      }
    }
    
    // Show summary toast
    if (successCount > 0) {
      toast.success(`${successCount} file(s) added successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
    }
    
    // Refresh data once at the end
    fetchData();
    
    // Cleanup
    setIsAddingFile(false);
    setTimeout(() => {
      setAdditionalFileUploadStats(prev => {
        const newState = { ...prev };
        delete newState[lessonId];
        return newState;
      });
    }, 2000);
    e.target.value = ''; // Reset file input
  };

  // Handle removing a file from a lesson
  const handleRemoveFile = async (lessonId, fileId, fileName) => {
    if (!window.confirm(`Remove "${fileName || 'this file'}" from the lesson?`)) return;
    
    try {
      await api.delete(`/lessons/${lessonId}/files/${fileId}`);
      toast.success('File removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove file');
    }
  };

  // Handle replacing a file - opens file picker
  const handleReplaceFile = (lessonId, fileId) => {
    setReplaceFileTarget({ lessonId, fileId });
    // Trigger file input click
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.click();
    }
  };

  // Handle file selection for replace
  const handleReplaceFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceFileTarget) {
      setReplaceFileTarget(null);
      e.target.value = '';
      return;
    }

    const { lessonId, fileId } = replaceFileTarget;
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm', 'application/pdf', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac'];

    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: video, audio, PDF.');
      setReplaceFileTarget(null);
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('File too large (max 5GB)');
      setReplaceFileTarget(null);
      e.target.value = '';
      return;
    }

    setIsReplacingFile(true);
    toast.info(`Uploading replacement file: ${file.name}`);

    try {
      // Get file duration for video/audio
      let fileDuration = null;
      const isMediaFile = file.type.startsWith('video/') || file.type.startsWith('audio/');
      
      if (isMediaFile) {
        try {
          fileDuration = await new Promise((resolve, reject) => {
            const mediaElement = file.type.startsWith('video/') 
              ? document.createElement('video')
              : document.createElement('audio');
            mediaElement.preload = 'metadata';
            mediaElement.onloadedmetadata = () => {
              URL.revokeObjectURL(mediaElement.src);
              resolve(mediaElement.duration);
            };
            mediaElement.onerror = () => {
              URL.revokeObjectURL(mediaElement.src);
              resolve(null);
            };
            mediaElement.src = URL.createObjectURL(file);
          });
        } catch (err) {
          console.warn('Could not get media duration:', err);
        }
      }

      // Upload file to R2
      const { promise: uploadPromise } = uploadVideoToS3(file, 'courses/', (progress) => {
        // Could add progress UI here if needed
      });
      
      const uploadResult = await uploadPromise;

      // Determine file type
      let fileType = 'video';
      if (file.type === 'application/pdf') {
        fileType = 'pdf';
      } else if (file.type.startsWith('audio/')) {
        fileType = 'audio';
      }

      // Call backend to replace file
      await api.put(`/lessons/${lessonId}/files/${fileId}/replace`, {
        url: uploadResult.url,
        file_type: fileType,
        file_name: file.name,
        file_size: file.size,
        duration: fileDuration
      });

      toast.success('File replaced successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to replace file');
    } finally {
      setIsReplacingFile(false);
      setReplaceFileTarget(null);
      e.target.value = '';
    }
  };

  // Handle replacing the primary file - opens file picker
  const handleReplacePrimaryFile = (lessonId) => {
    setReplacePrimaryFileTarget(lessonId);
    if (replacePrimaryFileInputRef.current) {
      replacePrimaryFileInputRef.current.click();
    }
  };

  // Handle file selection for primary file replace
  const handleReplacePrimaryFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacePrimaryFileTarget) {
      setReplacePrimaryFileTarget(null);
      e.target.value = '';
      return;
    }

    const lessonId = replacePrimaryFileTarget;
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/mpeg', 'video/webm', 'application/pdf', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac'];

    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: video, audio, PDF.');
      setReplacePrimaryFileTarget(null);
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024 * 1024) {
      toast.error('File too large (max 5GB)');
      setReplacePrimaryFileTarget(null);
      e.target.value = '';
      return;
    }

    setIsReplacingPrimaryFile(true);
    toast.info(`Uploading replacement file: ${file.name}`);

    try {
      // Upload file to R2
      const { promise: uploadPromise } = uploadVideoToS3(file, 'courses/', (progress) => {
        // Could add progress UI here if needed
      });
      
      const uploadResult = await uploadPromise;

      // Update the lesson with the new primary file
      await api.patch(`/lessons/${lessonId}`, {
        video_url: uploadResult.url,
        video_type: 'upload',
        primary_file_name: file.name
      });

      toast.success('Primary file replaced successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to replace primary file');
    } finally {
      setIsReplacingPrimaryFile(false);
      setReplacePrimaryFileTarget(null);
      e.target.value = '';
    }
  };

  const handleDeletePrimaryFile = async (lessonId) => {
    if (!window.confirm('Delete the primary file from this lesson? The lesson will remain but without its main content.')) return;
    
    try {
      await api.patch(`/lessons/${lessonId}`, {
        video_url: '',
        video_type: 'upload',
        primary_file_name: ''
      });
      toast.success('Primary file removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove primary file');
    }
  };

  const openRenameDialog = (lessonId, fileId, fileName, isPrimary = false) => {
    setShowRenameDialog({ lessonId, fileId, fileName, isPrimary });
    setNewFileName(fileName || '');
  };

  const handleRenameFile = async () => {
    if (!showRenameDialog || !newFileName.trim()) return;
    
    setIsRenaming(true);
    try {
      if (showRenameDialog.isPrimary) {
        // Rename primary file - update lesson's primary_file_name field
        await api.patch(`/lessons/${showRenameDialog.lessonId}`, {
          primary_file_name: newFileName.trim()
        });
      } else {
        // Rename additional file
        await api.patch(`/lessons/${showRenameDialog.lessonId}/files/${showRenameDialog.fileId}/rename`, {
          file_name: newFileName.trim()
        });
      }
      toast.success('File renamed successfully');
      setShowRenameDialog(null);
      setNewFileName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to rename file');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleAddLiveSession = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.post('/live-sessions', {
        course_id: courseId,
        title: formData.get('title'),
        scheduled_at: new Date(formData.get('scheduled_at')).toISOString(),
        meeting_link: formData.get('meeting_link'),
        duration: parseInt(formData.get('duration')),
      });
      
      toast.success('Live session scheduled!');
      setShowAddSession(false);
      fetchData();
      e.target.reset();
    } catch (error) {
      toast.error('Failed to schedule session');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    
    try {
      await api.delete(`/lessons/${lessonId}`);
      toast.success('Lesson deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  // Move lesson up or down in the order
  const handleMoveLesson = async (lessonId, direction) => {
    try {
      await api.patch(`/lessons/${lessonId}/move?direction=${direction}`);
      fetchData(); // Refresh to show new order
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.detail;
      if (message) {
        toast.info(message);
      } else {
        toast.error('Failed to move lesson');
      }
    }
  };

  const handleViewMedia = (lesson) => {
    // Mark primary file as opened (per-device tracking)
    markFileOpened(`primary:${lesson.id}`);
    setSelectedMedia(lesson);
  };

  // View additional file - opens in the same media viewer modal as primary file
  const handleViewAdditionalFile = (file) => {
    // Mark file as opened (per-device tracking)
    markFileOpened(file.id);
    
    // Create a pseudo-lesson object to use the same media viewer
    const fileUrl = file.url.startsWith('http') ? file.url : file.url;
    const isPdf = file.file_type === 'pdf' || file.url?.endsWith('.pdf');
    
    const pseudoLesson = {
      id: file.id,
      title: file.file_name || file.url?.split('/').pop() || 'File',
      description: `Additional ${isPdf ? 'PDF document' : 'video'} for this lesson`,
      video_url: fileUrl,
      video_type: isPdf ? 'upload' : 'upload' // uploaded file type
    };
    
    setSelectedMedia(pseudoLesson);
  };

  // Download file - opens presigned URL with Content-Disposition: attachment
  const handleDownloadFile = async (fileUrl) => {
    try {
      const fileKey = extractFileKey(fileUrl);
      if (!fileKey) {
        toast.error('Unable to extract file key');
        return;
      }
      
      const result = await getPresignedDownloadUrl(fileKey, courseId, true);
      if (result?.download_url) {
        window.open(result.download_url, '_blank');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download file');
    }
  };

  // File reordering handlers
  const handleFileReorder = useCallback(async (lessonId, newFileOrder) => {
    try {
      await api.patch(`/lessons/${lessonId}/files/reorder`, {
        file_ids: newFileOrder
      });
      fetchData(); // Refresh to show new order
    } catch (error) {
      toast.error('Failed to reorder files');
      fetchData(); // Refresh to reset state
    }
  }, []);

  // Handle drag end for file reordering
  const handleFileDragEnd = useCallback((lessonId, files) => (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = files.findIndex(f => f.id === active.id);
      const newIndex = files.findIndex(f => f.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newFiles = arrayMove(files, oldIndex, newIndex);
        const newFileOrder = newFiles.map(f => f.id);
        handleFileReorder(lessonId, newFileOrder);
      }
    }
  }, [handleFileReorder]);

  // Move file up in order
  const handleMoveFileUp = useCallback((lessonId, files, fileId) => {
    const index = files.findIndex(f => f.id === fileId);
    if (index > 0) {
      const newFiles = arrayMove(files, index, index - 1);
      const newFileOrder = newFiles.map(f => f.id);
      handleFileReorder(lessonId, newFileOrder);
    }
  }, [handleFileReorder]);

  // Move file down in order
  const handleMoveFileDown = useCallback((lessonId, files, fileId) => {
    const index = files.findIndex(f => f.id === fileId);
    if (index < files.length - 1) {
      const newFiles = arrayMove(files, index, index + 1);
      const newFileOrder = newFiles.map(f => f.id);
      handleFileReorder(lessonId, newFileOrder);
    }
  }, [handleFileReorder]);

  // Move file to specific position
  const handleMoveFileToPosition = useCallback((lessonId, files, fileId, newIndex) => {
    const currentIndex = files.findIndex(f => f.id === fileId);
    if (currentIndex !== -1 && newIndex >= 0 && newIndex < files.length) {
      const newFiles = arrayMove(files, currentIndex, newIndex);
      const newFileOrder = newFiles.map(f => f.id);
      handleFileReorder(lessonId, newFileOrder);
    }
  }, [handleFileReorder]);

  // DnD sensors for file reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Quiz Management Functions
  const openQuizDialog = (lessonId) => {
    setQuizForm({
      title: '',
      questions: [{ question: '', options: ['', '', '', ''], correct_answer: 0 }]
    });
    setShowQuizDialog(lessonId);
  };

  const addQuestion = () => {
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, { question: '', options: ['', '', '', ''], correct_answer: 0 }]
    }));
  };

  const removeQuestion = (index) => {
    if (quizForm.questions.length <= 1) return;
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, options: q.options.map((o, oi) => oi === optionIndex ? value : o) }
          : q
      )
    }));
  };

  const handleSaveQuiz = async () => {
    if (!quizForm.title.trim()) {
      toast.error('Please enter a quiz title');
      return;
    }

    // Validate all questions
    for (let i = 0; i < quizForm.questions.length; i++) {
      const q = quizForm.questions[i];
      if (!q.question.trim()) {
        toast.error(`Please enter question ${i + 1}`);
        return;
      }
      const filledOptions = q.options.filter(o => o.trim());
      if (filledOptions.length < 2) {
        toast.error(`Question ${i + 1} needs at least 2 options`);
        return;
      }
    }

    setIsSavingQuiz(true);
    try {
      await api.post(`/lessons/${showQuizDialog}/quizzes`, {
        title: quizForm.title,
        questions: quizForm.questions.map(q => ({
          question: q.question,
          options: q.options.filter(o => o.trim()),
          correct_answer: q.correct_answer
        }))
      });
      toast.success('Quiz created successfully!');
      setShowQuizDialog(null);
      fetchAllLessonQuizzes(lessons);
    } catch (error) {
      toast.error('Failed to create quiz');
    } finally {
      setIsSavingQuiz(false);
    }
  };

  const handleDeleteQuiz = async (quizId, lessonId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    
    try {
      await api.delete(`/quizzes/${quizId}`);
      toast.success('Quiz deleted');
      fetchAllLessonQuizzes(lessons);
    } catch (error) {
      toast.error('Failed to delete quiz');
    }
  };

  const renderMediaPreview = (lesson) => {
    return <MediaPreview lesson={lesson} courseId={courseId} />;
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#163329' }}></div>
    </div>;
  }

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="course-manage-page">
      {/* Hidden file input for file replacement */}
      <input
        type="file"
        ref={replaceFileInputRef}
        accept="video/mp4,video/quicktime,video/x-msvideo,video/mpeg,video/webm,.mp4,.mov,.avi,.mpeg,.webm,application/pdf,.pdf,audio/mpeg,audio/wav,audio/ogg,audio/aac,.mp3,.wav,.ogg,.aac"
        onChange={handleReplaceFileSelect}
        style={{ display: 'none' }}
        data-testid="replace-file-input"
      />
      {/* Hidden file input for primary file replacement */}
      <input
        type="file"
        ref={replacePrimaryFileInputRef}
        accept="video/mp4,video/quicktime,video/x-msvideo,video/mpeg,video/webm,.mp4,.mov,.avi,.mpeg,.webm,application/pdf,.pdf,audio/mpeg,audio/wav,audio/ogg,audio/aac,.mp3,.wav,.ogg,.aac"
        onChange={handleReplacePrimaryFileSelect}
        style={{ display: 'none' }}
        data-testid="replace-primary-file-input"
      />
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Manage Course: {course?.title}
            </h1>
            {course && (
              <CourseIconPicker
                courseId={courseId}
                value={course.icon}
                imageUrl={course.thumbnail_url}
                onChange={(icon) => setCourse((prev) => ({ ...prev, icon }))}
                onImageChange={(thumbnail_url) => setCourse((prev) => ({ ...prev, thumbnail_url }))}
              />
            )}
            <Dialog open={showEditCourse} onOpenChange={setShowEditCourse}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full"
                  data-testid="edit-course-button"
                >
                  <Edit2 className="mr-2 h-4 w-4" />
                  Edit Course Details
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="edit-course-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    Edit Course Details
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="edit-title">Course Title</Label>
                    <Input 
                      id="edit-title" 
                      value={editCourseForm.title}
                      onChange={(e) => setEditCourseForm(prev => ({ ...prev, title: e.target.value }))}
                      data-testid="edit-course-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea 
                      id="edit-description" 
                      value={editCourseForm.description}
                      onChange={(e) => setEditCourseForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      data-testid="edit-course-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-price">Price (USD)</Label>
                    <Input 
                      id="edit-price" 
                      type="number"
                      min="0"
                      step="0.01"
                      value={editCourseForm.price}
                      onChange={(e) => setEditCourseForm(prev => ({ ...prev, price: e.target.value }))}
                      data-testid="edit-course-price-input"
                    />
                  </div>
                  
                  {/* Brochure Section */}
                  <div className="border-t pt-4 mt-4" style={{ borderColor: '#E3E1DB' }}>
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#163329' }}>
                      <FileText className="h-4 w-4" />
                      Course Brochure
                    </h4>
                    
                    {/* Brochure Text */}
                    <div className="mb-4">
                      <Label htmlFor="edit-brochure-text">Brochure Text (optional)</Label>
                      <Textarea 
                        id="edit-brochure-text" 
                        value={editCourseForm.brochure_text || ''}
                        onChange={(e) => setEditCourseForm(prev => ({ ...prev, brochure_text: e.target.value }))}
                        rows={4}
                        placeholder="Enter course brochure text or promotional description..."
                        data-testid="edit-brochure-text-input"
                      />
                    </div>
                    
                    {/* Brochure PDF Upload */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Brochure PDF Files ({(editCourseForm.brochure_files || []).length}/5)</Label>
                      </div>
                      
                      {/* List of existing brochure files */}
                      {(editCourseForm.brochure_files || []).length > 0 && (
                        <div className="mb-3">
                          <BrochureFileList
                            courseId={courseId}
                            files={editCourseForm.brochure_files || []}
                            onReplace={handleReplaceBrochure}
                            onDelete={handleRemoveBrochure}
                            busyIndex={replacingBrochureIndex}
                          />
                        </div>
                      )}
                      
                      {/* Upload button or max reached message */}
                      {(editCourseForm.brochure_files || []).length < 5 ? (
                        <div>
                          <input
                            ref={brochureFileInputRef}
                            type="file"
                            accept=".pdf"
                            onChange={handleBrochureUpload}
                            className="hidden"
                            id="brochure-upload"
                          />
                          <label htmlFor="brochure-upload">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isUploadingBrochure}
                              onClick={() => brochureFileInputRef.current?.click()}
                              className="w-full"
                              data-testid="upload-brochure-btn"
                            >
                              {isUploadingBrochure ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Uploading... {brochureUploadProgress}%
                                </>
                              ) : (
                                <>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Add Brochure PDF
                                </>
                              )}
                            </Button>
                          </label>
                          {isUploadingBrochure && (
                            <Progress value={brochureUploadProgress} className="mt-2" />
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-center py-2" style={{ color: '#5C635A' }}>
                          Maximum of 5 brochure files reached
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowEditCourse(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleEditCourse}
                      disabled={isEditingCourse || isUploadingBrochure}
                      className="flex-1"
                      
                      data-testid="save-course-button"
                    >
                      {isEditingCourse ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="primary" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1 justify-start w-full">
            <TabWithTooltip 
              value="lessons" 
              title="Lessons" 
              description="Manage course content and video lessons"
              data-testid="lessons-tab" 
              className="text-xs sm:text-sm"
            >
              Lessons
            </TabWithTooltip>
            <TabWithTooltip 
              value="kit" 
              title="Learning Kit" 
              description="Configure physical supplies students need for the course"
              data-testid="kit-tab" 
              className="text-xs sm:text-sm"
            >
              <Package className="h-3 w-3 mr-1 inline" />
              Learning Kit
            </TabWithTooltip>
            <TabWithTooltip 
              value="students" 
              title="Students" 
              description="View enrolled students and their progress"
              data-testid="students-tab" 
              className="text-xs sm:text-sm"
            >
              Students {enrolledStudents.length > 0 && `(${enrolledStudents.length})`}
            </TabWithTooltip>
            <TabWithTooltip 
              value="sessions" 
              title="Live Sessions" 
              description="Schedule and manage video conferencing sessions"
              data-testid="sessions-tab" 
              className="text-xs sm:text-sm"
            >
              Live Sessions
            </TabWithTooltip>
            <TabWithTooltip 
              value="terms" 
              title="Terms/Declaration/Letters" 
              description="Set course terms and conditions for students"
              data-testid="terms-tab" 
              className="text-xs sm:text-sm"
            >
              Terms/Declaration/Letters
            </TabWithTooltip>
            <TabWithTooltip 
              value="settings" 
              title="Settings" 
              description="Configure course visibility and enrollment options"
              data-testid="settings-tab" 
              className="text-xs sm:text-sm"
            >
              Settings
            </TabWithTooltip>
            <TabWithTooltip 
              value="gallery" 
              title="Gallery" 
              description="Manage course gallery images"
              data-testid="gallery-tab" 
              className="text-xs sm:text-sm"
            >
              <Image className="h-3 w-3 mr-1 inline" />
              Gallery {galleryImages.length > 0 && `(${galleryImages.length})`}
            </TabWithTooltip>
            <TabWithTooltip 
              value="brochure" 
              title="Brochure" 
              description="Manage course brochure text and PDF files"
              data-testid="brochure-tab" 
              className="text-xs sm:text-sm"
            >
              <FileText className="h-3 w-3 mr-1 inline" />
              Brochure {(editCourseForm.brochure_files?.length > 0 || editCourseForm.brochure_text) && '●'}
            </TabWithTooltip>
            <TabWithTooltip 
              value="certificates" 
              title="Certificates" 
              description="Configure certificate template, terms, and manage student certifications"
              data-testid="certificates-tab" 
              className="text-xs sm:text-sm"
            >
              <Award className="h-3 w-3 mr-1 inline" />
              Certificates
            </TabWithTooltip>
          </TabsList>

          <TabsContent value="lessons">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Lessons</h2>
              <Dialog open={showAddLesson} onOpenChange={(open) => {
                setShowAddLesson(open);
                if (open) {
                  // Reset form state when dialog opens
                  setNewLessonForm({ title: '', description: '', video_url: '', duration: '' });
                  setLessonVideoType('youtube');
                  setUploadProgress(0);
                  setIsUploading(false);
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="rounded-full"  data-testid="add-lesson-button">
                    <Plus className="mr-2 h-4 w-4" />Add Lesson
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-lesson-modal">
                  <DialogHeader>
                    <DialogTitle>Add New Lesson</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddLesson} className="space-y-4">
                    <div>
                      <Label htmlFor="title">Lesson Title</Label>
                      <Input id="title" name="title" required data-testid="lesson-title-input" />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea id="description" name="description" required data-testid="lesson-description-input" />
                    </div>
                    <div>
                      <Label>Content Type</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="radio" 
                            checked={lessonVideoType === 'youtube'}
                            onChange={() => setLessonVideoType('youtube')}
                            className="h-4 w-4"
                            style={{ accentColor: '#163329' }}
                          />
                          <span>YouTube URL</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input 
                            type="radio" 
                            checked={lessonVideoType === 'uploaded'}
                            onChange={() => setLessonVideoType('uploaded')}
                            className="h-4 w-4"
                            style={{ accentColor: '#163329' }}
                          />
                          <span>Upload File</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="video_url">
                        {lessonVideoType === 'youtube' ? 'YouTube Video URL' : 'File Path (auto-filled after upload)'}
                      </Label>
                      <Input 
                        id="video_url" 
                        name="video_url" 
                        required 
                        data-testid="lesson-video-url-input" 
                        placeholder={lessonVideoType === 'youtube' ? 'https://youtube.com/watch?v=...' : 'Upload file below to auto-fill'}
                        readOnly={lessonVideoType === 'uploaded'}
                        value={newLessonForm.video_url}
                        onChange={(e) => setNewLessonForm(prev => ({ ...prev, video_url: e.target.value }))}
                      />
                      {lessonVideoType === 'uploaded' && (
                        <div className="mt-2">
                          <Label className="text-sm" style={{ color: '#5C635A' }}>Upload Video or PDF:</Label>
                          
                          <input 
                            type="file" 
                            accept="video/mp4,video/quicktime,video/x-msvideo,video/mpeg,video/webm,.mp4,.mov,.avi,.mpeg,.webm,application/pdf,.pdf,audio/mpeg,audio/wav,audio/ogg,audio/aac,.mp3,.wav,.ogg,.aac" 
                            onChange={handleVideoUpload} 
                            className="text-sm w-full mt-1 p-2 border rounded"
                            style={{ borderColor: '#E3E1DB' }}
                            data-testid="lesson-file-upload"
                            disabled={isUploading || uploadStats.isProcessing}
                          />
                          
                          {/* Enhanced Upload Progress UI */}
                          {(isUploading || uploadStats.isProcessing) && (
                            <div className="mt-3 space-y-3 p-4 rounded-lg" style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}>
                              {/* Progress Header */}
                              <div className="flex justify-between items-center">
                                <span style={{ color: '#163329' }} className="font-medium">
                                  {uploadStats.isProcessing ? 'Processing Video' : 'Uploading...'}
                                </span>
                                <span style={{ color: '#D86B47' }} className="font-bold text-lg">
                                  {uploadStats.percentage}%
                                </span>
                              </div>
                              
                              {/* Progress Bar */}
                              <Progress 
                                value={uploadStats.percentage} 
                                className="h-3"
                                style={{ backgroundColor: '#E3E1DB' }}
                              />
                              
                              {/* Stats Row */}
                              <div className="flex justify-between text-xs" style={{ color: '#5C635A' }}>
                                <span>
                                  {(uploadStats.bytesUploaded / (1024 * 1024)).toFixed(1)} MB / {(uploadStats.bytesTotal / (1024 * 1024)).toFixed(1)} MB
                                </span>
                                {uploadStats.estimatedSecondsRemaining !== null && !uploadStats.isProcessing && (
                                  <span className="font-medium" style={{ color: '#163329' }}>
                                    {formatTimeRemaining(uploadStats.estimatedSecondsRemaining)}
                                  </span>
                                )}
                              </div>
                              
                              {/* Status Message */}
                              <div className="flex items-center gap-2">
                                {uploadStats.isProcessing ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#D86B47' }} />
                                    <span className="text-sm" style={{ color: '#5C635A' }}>
                                      {uploadStats.processingMessage || 'Cloudflare is transcoding your video...'}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-sm" style={{ color: '#5C635A' }}>
                                    {uploadStats.percentage < 100 
                                      ? 'Please wait while your file uploads...' 
                                      : 'Upload complete, starting processing...'}
                                  </span>
                                )}
                              </div>
                              
                              {/* Processing Info */}
                              {uploadStats.isProcessing && (
                                <p className="text-xs" style={{ color: '#5C635A', borderTop: '1px solid #E3E1DB', paddingTop: '8px' }}>
                                  Video transcoding typically takes 30 seconds to a few minutes depending on file size.
                                </p>
                              )}
                              
                              {/* Cancel Upload Button */}
                              {!uploadStats.isProcessing && (
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={handleCancelMainUpload}
                                  className="w-full mt-2"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel Upload
                                </Button>
                              )}
                            </div>
                          )}
                          
                          {/* Upload Success Message */}
                          {newLessonForm.video_url && !isUploading && !uploadStats.isProcessing && (
                            <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}>
                              <div className="flex items-center gap-2">
                                <span style={{ color: '#2E7D32' }}>✓</span>
                                <span className="text-sm font-medium" style={{ color: '#2E7D32' }}>
                                  File uploaded successfully
                                </span>
                              </div>
                              <p className="text-xs mt-1 truncate" style={{ color: '#5C635A' }}>
                                {newLessonForm.video_url.split('/').pop()}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="duration">Duration (minutes)</Label>
                      <Input id="duration" name="duration" type="number" data-testid="lesson-duration-input" />
                    </div>
                    
                    {/* Individual Purchase Settings */}
                    <div className="border-t pt-4 mt-4" style={{ borderColor: '#E3E1DB' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <Label htmlFor="is_purchasable" className="text-sm font-medium">Available for individual purchase</Label>
                          <p className="text-xs" style={{ color: '#5C635A' }}>Allow students to buy this lesson separately</p>
                        </div>
                        <input
                          type="checkbox"
                          id="is_purchasable"
                          checked={newLessonForm.is_purchasable}
                          onChange={(e) => setNewLessonForm(prev => ({ ...prev, is_purchasable: e.target.checked }))}
                          className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          data-testid="lesson-purchasable-toggle"
                        />
                      </div>
                      {newLessonForm.is_purchasable && (
                        <div>
                          <Label htmlFor="lesson_price">Lesson Price (USD)</Label>
                          <Input 
                            id="lesson_price" 
                            type="number" 
                            min="0" 
                            step="0.01"
                            value={newLessonForm.price}
                            onChange={(e) => setNewLessonForm(prev => ({ ...prev, price: e.target.value }))}
                            placeholder="0.00"
                            data-testid="lesson-price-input"
                          />
                        </div>
                      )}
                    </div>
                    
                    <Button type="submit" className="w-full"  data-testid="submit-lesson">
                      Add Lesson
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {lessons.length === 0 ? (
                <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <Video className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                  <p style={{ color: '#5C635A' }}>No lessons yet. Add your first lesson!</p>
                </Card>
              ) : (
                lessons.map((lesson, index) => (
                  <Card key={lesson.id} className="p-4 sm:p-6" style={{ borderColor: '#E3E1DB' }} data-testid={`lesson-item-${lesson.id}`}>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        {/* Reorder buttons - vertical stack */}
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMoveLesson(lesson.id, 'up')}
                            disabled={index === 0}
                            className="h-7 w-7 p-0"
                            style={{ color: index === 0 ? '#ccc' : '#163329' }}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMoveLesson(lesson.id, 'down')}
                            disabled={index === lessons.length - 1}
                            className="h-7 w-7 p-0"
                            style={{ color: index === lessons.length - 1 ? '#ccc' : '#163329' }}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>
                            {index + 1}. {lesson.title}
                          </h3>
                          <p className="text-sm mt-1" style={{ color: '#5C635A' }}>{lesson.description}</p>
                          {lesson.duration && <p className="text-sm mt-1" style={{ color: '#5C635A' }}>{lesson.duration} min</p>}
                          <p className="text-xs mt-2" style={{ color: '#5C635A' }}>
                            Type: {lesson.video_type === 'youtube' ? 'YouTube' : lesson.video_url?.endsWith('.pdf') ? 'PDF' : 'Video'}
                          </p>
                        </div>
                      </div>
                      {/* Action buttons - wrap on mobile */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: '#E3E1DB' }}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditLessonDialog(lesson)}
                          data-testid={`edit-lesson-${lesson.id}`}
                          className="flex-1 sm:flex-none"
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openMoveLessonDialog(lesson)}
                          data-testid={`move-lesson-${lesson.id}`}
                          className="flex-1 sm:flex-none"
                          style={{ color: '#0288D1', borderColor: '#0288D1' }}
                        >
                          <FolderInput className="h-4 w-4 mr-1" />
                          Move to Course
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteLesson(lesson.id)}
                          data-testid={`delete-lesson-${lesson.id}`}
                          className="flex-1 sm:flex-none text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 hover:border-red-400"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete Lesson
                        </Button>
                      </div>
                    </div>
                    
                    {/* Additional Files Section */}
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-medium flex items-center gap-2" style={{ color: '#163329' }}>
                          <FileText className="h-4 w-4" />
                          Lesson Materials ({(lesson.files?.length || 0) + (lesson.video_url ? 1 : 0)} files)
                          {teacherOpenedFiles.filter(fid => lesson.files?.some(f => f.id === fid)).length > 0 && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#E1F5FE', color: '#0288D1' }}>
                              {teacherOpenedFiles.filter(fid => lesson.files?.some(f => f.id === fid)).length} viewed
                            </span>
                          )}
                        </h4>
                        <div className="flex items-center gap-2">
                          {/* Clear opened indicator button */}
                          {teacherOpenedFiles.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={clearOpenedFiles}
                              className="text-xs h-8 px-2"
                              style={{ color: '#0288D1' }}
                              title="Clear all 'viewed' indicators"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Clear viewed
                            </Button>
                          )}
                          <div className="relative">
                            <input
                              type="file"
                              accept="video/mp4,video/quicktime,video/x-msvideo,video/mpeg,video/webm,.mp4,.mov,.avi,.mpeg,.webm,application/pdf,.pdf,audio/mpeg,audio/wav,audio/ogg,audio/aac,.mp3,.wav,.ogg,.aac"
                              onChange={(e) => handleAdditionalFileUpload(e, lesson.id)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              disabled={additionalFileUploadStats[lesson.id]?.isUploading || isAddingFile}
                              multiple
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={additionalFileUploadStats[lesson.id]?.isUploading || isAddingFile}
                              className="text-xs"
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              {additionalFileUploadStats[lesson.id]?.isUploading ? 'Uploading...' : 'Add Files'}
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional File Upload Progress */}
                      {additionalFileUploadStats[lesson.id]?.isUploading && (
                        <div className="mb-3 p-3 rounded-lg space-y-2" style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}>
                          <div className="flex justify-between items-center">
                            <span style={{ color: '#163329' }} className="font-medium text-sm">
                              {additionalFileUploadStats[lesson.id]?.totalFiles > 1 
                                ? `Uploading file ${additionalFileUploadStats[lesson.id]?.currentFileIndex} of ${additionalFileUploadStats[lesson.id]?.totalFiles}`
                                : 'Uploading file...'}
                            </span>
                            <span style={{ color: '#D86B47' }} className="font-bold">
                              {additionalFileUploadStats[lesson.id]?.percentage || 0}%
                            </span>
                          </div>
                          {additionalFileUploadStats[lesson.id]?.currentFileName && (
                            <p className="text-xs truncate" style={{ color: '#5C635A' }}>
                              {additionalFileUploadStats[lesson.id]?.currentFileName}
                            </p>
                          )}
                          <Progress 
                            value={additionalFileUploadStats[lesson.id]?.percentage || 0} 
                            className="h-2"
                            style={{ backgroundColor: '#E3E1DB' }}
                          />
                          <div className="flex justify-between text-xs" style={{ color: '#5C635A' }}>
                            <span>
                              {((additionalFileUploadStats[lesson.id]?.bytesUploaded || 0) / (1024 * 1024)).toFixed(1)} MB / {((additionalFileUploadStats[lesson.id]?.bytesTotal || 0) / (1024 * 1024)).toFixed(1)} MB
                            </span>
                            {additionalFileUploadStats[lesson.id]?.estimatedSecondsRemaining !== null && (
                              <span className="font-medium" style={{ color: '#163329' }}>
                                {formatTimeRemaining(additionalFileUploadStats[lesson.id]?.estimatedSecondsRemaining)}
                              </span>
                            )}
                          </div>
                          {/* Cancel Upload Button */}
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancelAdditionalFileUpload(lesson.id)}
                            className="w-full mt-2"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel Upload
                          </Button>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        {/* Primary file - marked with "1" badge - only show if video_url exists */}
                        {lesson.video_url && (() => {
                          const isPrimaryOpened = teacherOpenedFiles.includes(`primary:${lesson.id}`);
                          // Colors: Green (default) or Skyblue (opened)
                          const bgColor = isPrimaryOpened ? '#E1F5FE' : '#E8F5E9';
                          const borderColor = isPrimaryOpened ? '#29B6F6' : '#4CAF50';
                          const accentColor = isPrimaryOpened ? '#0288D1' : '#2E7D32';
                          
                          return (
                        <div 
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: bgColor, border: `1px solid ${borderColor}` }}
                        >
                          {/* Row 1: Number badge + file-type icon + file-type Badge */}
                          <div className="flex items-center gap-2">
                            <Badge style={{ backgroundColor: accentColor, color: 'white', minWidth: '24px', justifyContent: 'center' }} className="text-xs font-bold flex-shrink-0">
                              1
                            </Badge>
                            {lesson.video_type === 'youtube' ? (
                              <Video className="h-5 w-5 flex-shrink-0" style={{ color: accentColor }} />
                            ) : lesson.video_url?.endsWith('.pdf') ? (
                              <FileText className="h-5 w-5 flex-shrink-0" style={{ color: accentColor }} />
                            ) : (
                              <Video className="h-5 w-5 flex-shrink-0" style={{ color: accentColor }} />
                            )}
                            <Badge variant="outline" style={{ borderColor: accentColor, color: accentColor }} className="text-xs flex-shrink-0">
                              {lesson.video_type === 'youtube' ? 'YouTube' : lesson.video_url?.endsWith('.pdf') ? 'PDF' : 'Video'}
                            </Badge>
                            {isPrimaryOpened && (
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#29B6F6', color: 'white' }}>
                                Opened
                              </span>
                            )}
                          </div>
                          
                          {/* Row 2: File name */}
                          <div className="mt-2 py-2">
                            <span className="font-medium text-sm break-words" style={{ color: accentColor }}>
                              {lesson.video_type === 'youtube' ? 'YouTube Video' : (lesson.primary_file_name || lesson.video_url?.split('/').pop() || 'Uploaded File')}
                            </span>
                          </div>
                          
                          {/* Row 3: Action buttons */}
                          <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t" style={{ borderColor: borderColor }}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewMedia(lesson)}
                              className="flex-none h-8 px-2 max-w-[100px]"
                              style={{ color: accentColor, borderColor: accentColor }}
                              title="View primary file"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">View</span>
                            </Button>
                            {lesson.video_type !== 'youtube' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRenameDialog(lesson.id, null, lesson.primary_file_name || lesson.video_url?.split('/').pop(), true)}
                                className="flex-none h-8 px-2 max-w-[100px]"
                                style={{ color: accentColor, borderColor: accentColor }}
                                title="Rename primary file"
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1">Rename</span>
                              </Button>
                            )}
                            {lesson.video_type !== 'youtube' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReplacePrimaryFile(lesson.id)}
                                disabled={isReplacingPrimaryFile}
                                className="flex-none h-8 px-2 max-w-[100px]"
                                style={{ color: accentColor, borderColor: accentColor }}
                                title="Replace primary file"
                              >
                                <RefreshCw className={`h-4 w-4 ${isReplacingPrimaryFile ? 'animate-spin' : ''}`} />
                                <span className="hidden sm:inline ml-1">Replace</span>
                              </Button>
                            )}
                            {lesson.video_type !== 'youtube' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadFile(lesson.video_url)}
                                className="flex-none h-8 px-2 max-w-[100px]"
                                style={{ color: accentColor, borderColor: accentColor }}
                                title="Download primary file"
                              >
                                <Download className="h-4 w-4" />
                                <span className="hidden sm:inline ml-1">Download</span>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeletePrimaryFile(lesson.id)}
                              className="flex-none h-8 px-2 max-w-[100px] text-red-500 hover:text-red-600 hover:bg-red-50"
                              style={{ borderColor: '#ef4444' }}
                              title="Delete primary file"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">Delete</span>
                            </Button>
                          </div>
                        </div>
                          );
                        })()}
                        
                        {/* Additional files - with drag-and-drop reordering */}
                        {lesson.files && lesson.files.length > 0 && (
                          <>
                            {/* Search box for filtering files */}
                            <div className="relative mb-2">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                type="text"
                                placeholder="Search files by name..."
                                value={lessonFileSearchQueries[lesson.id] || ''}
                                onChange={(e) => setLessonFileSearchQueries(prev => ({ ...prev, [lesson.id]: e.target.value }))}
                                className="pl-10 h-9 text-sm"
                                style={{ borderColor: '#E3E1DB' }}
                              />
                            </div>
                            
                            <ScrollArea className="h-[320px] pr-2" style={{ height: '320px' }}>
                              <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleFileDragEnd(lesson.id, lesson.files)}
                              >
                                <SortableContext
                                  items={lesson.files
                                    .filter(file => {
                                      const query = lessonFileSearchQueries[lesson.id]?.toLowerCase().trim();
                                      if (!query) return true;
                                      return (file.file_name || '').toLowerCase().includes(query);
                                    })
                                    .map(f => f.id)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {lesson.files
                                    .filter(file => {
                                      const query = lessonFileSearchQueries[lesson.id]?.toLowerCase().trim();
                                      if (!query) return true;
                                      return (file.file_name || '').toLowerCase().includes(query);
                                    })
                                    .map((file, index) => (
                                      <SortableFileItem
                                        key={file.id}
                                        file={file}
                                        index={index}
                                        totalFiles={lesson.files.filter(f => {
                                          const query = lessonFileSearchQueries[lesson.id]?.toLowerCase().trim();
                                          if (!query) return true;
                                          return (f.file_name || '').toLowerCase().includes(query);
                                        }).length}
                                        lessonVideoUrl={lesson.video_url}
                                        onMoveUp={(lessonFileSearchQueries[lesson.id]?.trim()) ? undefined : ((fileId) => handleMoveFileUp(lesson.id, lesson.files, fileId))}
                                        onMoveDown={(lessonFileSearchQueries[lesson.id]?.trim()) ? undefined : ((fileId) => handleMoveFileDown(lesson.id, lesson.files, fileId))}
                                        onOrderChange={(lessonFileSearchQueries[lesson.id]?.trim()) ? undefined : ((fileId, newIndex) => handleMoveFileToPosition(lesson.id, lesson.files, fileId, newIndex))}
                                        onView={(file) => handleViewAdditionalFile(file)}
                                        onRename={(file) => openRenameDialog(lesson.id, file.id, file.file_name)}
                                        onReplace={(file) => handleReplaceFile(lesson.id, file.id)}
                                        onDownload={(file) => handleDownloadFile(file.url)}
                                        onDelete={(file) => handleRemoveFile(lesson.id, file.id, file.file_name)}
                                        onMove={(file) => openMoveFileDialog(lesson.id, file)}
                                        disableDrag={!!(lessonFileSearchQueries[lesson.id]?.trim())}
                                        isOpened={teacherOpenedFiles.includes(file.id)}
                                      />
                                    ))}
                                </SortableContext>
                              </DndContext>
                            </ScrollArea>
                            
                            {/* No files match message */}
                            {lessonFileSearchQueries[lesson.id]?.trim() && lesson.files.filter(file => {
                              const query = lessonFileSearchQueries[lesson.id]?.toLowerCase().trim();
                              return (file.file_name || '').toLowerCase().includes(query);
                            }).length === 0 && (
                              <p className="text-xs text-center py-3" style={{ color: '#5C635A' }}>
                                No files match "{lessonFileSearchQueries[lesson.id]}"
                              </p>
                            )}
                          </>
                        )}
                        
                        {(!lesson.files || lesson.files.length === 0) && (
                          <p className="text-xs text-center py-2" style={{ color: '#5C635A' }}>
                            Upload more videos, audio, or PDFs to supplement this lesson
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Quiz Section for this Lesson */}
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-medium flex items-center gap-2" style={{ color: '#163329' }}>
                          <ClipboardList className="h-4 w-4" />
                          Quizzes
                        </h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openQuizDialog(lesson.id)}
                          className="text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Quiz
                        </Button>
                      </div>
                      
                      {lessonQuizzes[lesson.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {lessonQuizzes[lesson.id].map((quiz) => (
                            <div 
                              key={quiz.id}
                              className="flex items-center justify-between p-3 rounded-lg"
                              style={{ backgroundColor: '#F0EFEA' }}
                            >
                              <div className="flex items-center gap-2">
                                <ClipboardList className="h-4 w-4" style={{ color: '#163329' }} />
                                <span className="font-medium text-sm" style={{ color: '#163329' }}>{quiz.title}</span>
                                <Badge variant="outline" className="text-xs">
                                  {quiz.questions?.length || 0} questions
                                </Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteQuiz(quiz.id, lesson.id)}
                                className="text-red-500 hover:text-red-600 h-7 w-7 p-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: '#5C635A' }}>
                          No quizzes yet. Add a quiz to test student knowledge.
                        </p>
                      )}
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Learning Kit Tab */}
          <TabsContent value="kit">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Package className="inline h-6 w-6 mr-2" />
                    Learning Kit
                  </h2>
                  <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                    Physical materials students will need for this course
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#E3F2FD', border: '1px solid #90CAF9' }}>
                <p className="text-sm" style={{ color: '#1565C0' }}>
                  List all materials/items students will need to complete this course. 
                  Insight Evolution will manufacture, arrange and supply these kits on your behalf.
                  Delivery typically takes 3-6 weeks. Prices may be adjusted by admin after verification.
                </p>
              </div>

              {/* Price Breakdown Card */}
              {course && (
                <Card className="p-4" style={{ borderColor: '#E3E1DB', backgroundColor: '#F0EFEA' }}>
                  <h3 className="font-medium mb-3" style={{ color: '#163329' }}>Course Price Breakdown</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span style={{ color: '#5C635A' }}>Base Course Price:</span>
                      <span style={{ color: '#163329' }}><Usd value={(course.base_price || 0)} /></span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: '#5C635A' }}>Kit Price:</span>
                      <span style={{ color: '#D86B47' }}><Usd value={totalKitPrice} /></span>
                    </div>
                    <div className="flex justify-between pt-2 border-t font-bold" style={{ borderColor: '#E3E1DB' }}>
                      <span style={{ color: '#163329' }}>Total Course Price:</span>
                      <span style={{ color: '#163329' }}><Usd value={((course.base_price || 0) + totalKitPrice)} /></span>
                    </div>
                  </div>
                </Card>
              )}

              {/* Kit Items */}
              <div className="space-y-3">
                {kitItems.length === 0 ? (
                  <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
                    <Package className="h-12 w-12 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
                    <p style={{ color: '#5C635A' }}>No kit items added yet</p>
                    <Button
                      onClick={() => {
                        addKitItem();
                        setShowKitSection(true);
                      }}
                      className="mt-4"
                      data-testid="add-first-kit-item"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Kit Item
                    </Button>
                  </Card>
                ) : (
                  <>
                    {kitItems.map((item, index) => (
                      <Card key={index} className="p-4" style={{ borderColor: '#E3E1DB', backgroundColor: '#F0EFEA' }}>
                        <div className="flex gap-3 items-start">
                          <div className="flex-1 space-y-3">
                            <div>
                              <Label className="text-xs" style={{ color: '#5C635A' }}>Item Name *</Label>
                              <Input
                                placeholder="Item name (e.g., Paint brush set)"
                                value={item.name || ''}
                                onChange={(e) => updateKitItem(index, 'name', e.target.value)}
                                data-testid={`kit-item-name-${index}`}
                                style={{ borderColor: '#E3E1DB' }}
                              />
                            </div>
                            <div className="flex gap-3 flex-wrap">
                              <div className="w-24">
                                <Label className="text-xs" style={{ color: '#5C635A' }}>Quantity</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  placeholder="Qty"
                                  value={item.quantity || 1}
                                  onChange={(e) => updateKitItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                  data-testid={`kit-item-qty-${index}`}
                                  style={{ borderColor: '#E3E1DB' }}
                                />
                              </div>
                              <div className="w-32">
                                <Label className="text-xs" style={{ color: '#5C635A' }}>Unit Price ($)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={item.unit_price || ''}
                                  onChange={(e) => updateKitItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                  data-testid={`kit-item-price-${index}`}
                                  style={{ borderColor: '#E3E1DB' }}
                                />
                              </div>
                              <div className="flex-1 min-w-[150px]">
                                <Label className="text-xs" style={{ color: '#5C635A' }}>Description</Label>
                                <Input
                                  placeholder="Optional description"
                                  value={item.description || ''}
                                  onChange={(e) => updateKitItem(index, 'description', e.target.value)}
                                  data-testid={`kit-item-desc-${index}`}
                                  style={{ borderColor: '#E3E1DB' }}
                                />
                              </div>
                            </div>
                            <div className="text-right text-sm font-medium" style={{ color: '#163329' }}>
                              Item Total: <Usd value={((item.quantity || 0) * (item.unit_price || 0))} />
                            </div>
                          </div>
                          {kitItems.length > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeKitItem(index)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-4"
                              data-testid={`remove-kit-item-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </Card>
                    ))}

                    {/* Kit Total Price Summary */}
                    <div className="p-4 rounded-lg" style={{ backgroundColor: '#163329' }}>
                      <div className="flex justify-between items-center text-white">
                        <span className="font-medium">Total Kit Price:</span>
                        <span className="text-xl font-bold" style={{ color: '#D86B47' }}><Usd value={totalKitPrice} /></span>
                      </div>
                      <p className="text-xs mt-2" style={{ color: '#E3E1DB' }}>
                        This amount will be added to your course base price. Final price may be adjusted by admin after kit verification.
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={addKitItem}
                        className="flex-1"
                        data-testid="add-kit-item-button"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Another Item
                      </Button>
                      <Button
                        onClick={handleSaveKit}
                        disabled={isSavingKit}
                        className="flex-1"
                        data-testid="save-kit-button"
                      >
                        {isSavingKit ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                        ) : (
                          <>Save Kit</>
                        )}
                      </Button>
                      <Button
                        onClick={handleDeleteKit}
                        variant="destructive"
                        className="flex-none"
                        data-testid="remove-kit-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Kit
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    Enrolled Students
                  </h2>
                  <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                    {enrolledStudents.length} student{enrolledStudents.length !== 1 ? 's' : ''} enrolled in this course
                  </p>
                </div>
              </div>

              {isLoadingStudents ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#163329' }}></div>
                </div>
              ) : enrolledStudents.length === 0 ? (
                <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <Users className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                  <p style={{ color: '#5C635A' }}>No students enrolled yet</p>
                  <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                    Students will appear here once they enroll in your course
                  </p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {enrolledStudents.map((item, index) => (
                    <Card 
                      key={item.student.id} 
                      className="p-6" 
                      style={{ borderColor: '#E3E1DB' }}
                      data-testid={`student-card-${item.student.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        {/* Avatar */}
                        <div
                          className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold overflow-hidden flex-shrink-0"
                          
                        >
                          {item.student.avatar_url ? (
                            <img 
                              src={item.student.avatar_url.startsWith('/api/') 
                                ? `${process.env.REACT_APP_BACKEND_URL}${item.student.avatar_url}` 
                                : item.student.avatar_url} 
                              alt={item.student.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            item.student.name?.charAt(0)?.toUpperCase() || 'S'
                          )}
                        </div>
                        
                        {/* Student Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>
                              {item.student.name}
                            </h3>
                            <Badge 
                              variant="outline" 
                              className="text-xs"
                              style={{ 
                                backgroundColor: item.enrollment.status === 'active' ? '#E8F5E9' : '#FEE2E2',
                                color: item.enrollment.status === 'active' ? '#2E7D32' : '#DC2626',
                                borderColor: 'transparent'
                              }}
                            >
                              {item.enrollment.status || 'Active'}
                            </Badge>
                          </div>
                          
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2 text-sm min-w-0" style={{ color: '#5C635A' }}>
                              <Mail className="h-4 w-4 flex-shrink-0" />
                              <a href={`mailto:${item.student.email}`} className="hover:underline min-w-0 truncate block max-w-full">
                                {item.student.email}
                              </a>
                            </div>
                            {item.student.phone && (
                              <div className="flex items-center gap-2 text-sm" style={{ color: '#5C635A' }}>
                                <Phone className="h-4 w-4 flex-shrink-0" />
                                <a href={`tel:${item.student.phone}`} className="hover:underline">
                                  {item.student.phone}
                                </a>
                              </div>
                            )}
                            {item.enrollment.enrolled_at && (
                              <div className="flex items-center gap-2 text-sm" style={{ color: '#5C635A' }}>
                                <Calendar className="h-4 w-4 flex-shrink-0" />
                                Enrolled: {new Date(item.enrollment.enrolled_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          
                          {/* Progress */}
                          <div className="mt-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span style={{ color: '#5C635A' }}>Course Progress</span>
                              <span className="font-medium" style={{ color: '#163329' }}>
                                {Math.round(item.enrollment.progress || 0)}%
                              </span>
                            </div>
                            <Progress value={item.enrollment.progress || 0} className="h-2" />
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0 w-full sm:w-auto">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/chat`)}
                            className="rounded-full"
                          >
                            <MessageCircle className="h-4 w-4 mr-1" />
                            Message
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Live Sessions</h2>
              <Dialog open={showAddSession} onOpenChange={setShowAddSession}>
                <DialogTrigger asChild>
                  <Button className="rounded-full"  data-testid="add-session-button">
                    <Plus className="mr-2 h-4 w-4" />Schedule Session
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="add-session-modal">
                  <DialogHeader>
                    <DialogTitle>Schedule Live Session</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddLiveSession} className="space-y-4">
                    <div>
                      <Label htmlFor="session-title">Session Title</Label>
                      <Input id="session-title" name="title" required data-testid="session-title-input" />
                    </div>
                    <div>
                      <Label htmlFor="scheduled_at">Date & Time</Label>
                      <Input id="scheduled_at" name="scheduled_at" type="datetime-local" required data-testid="session-datetime-input" />
                    </div>
                    <div>
                      <Label htmlFor="meeting_link">Meeting Link (Zoom/Google Meet)</Label>
                      <Input id="meeting_link" name="meeting_link" type="url" required placeholder="https://zoom.us/j/..." data-testid="session-link-input" />
                    </div>
                    <div>
                      <Label htmlFor="session-duration">Duration (minutes)</Label>
                      <Input id="session-duration" name="duration" type="number" defaultValue={60} required data-testid="session-duration-input" />
                    </div>
                    <Button type="submit" className="w-full"  data-testid="submit-session">
                      Schedule Session
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {liveSessions.length === 0 ? (
                <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <Calendar className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                  <p style={{ color: '#5C635A' }}>No live sessions scheduled yet</p>
                </Card>
              ) : (
                liveSessions.map((session) => (
                  <Card key={session.id} className="p-6" style={{ borderColor: '#E3E1DB' }} data-testid={`session-item-${session.id}`}>
                    <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>{session.title}</h3>
                    <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                      {new Date(session.scheduled_at).toLocaleString()}
                    </p>
                    <p className="text-sm" style={{ color: '#5C635A' }}>{session.duration} minutes</p>
                    <a href={session.meeting_link} target="_blank" rel="noopener noreferrer" className="text-sm underline" style={{ color: '#D86B47' }}>
                      {session.meeting_link}
                    </a>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Terms & Conditions Tab */}
          <TabsContent value="terms">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    Course Terms & Conditions
                  </h2>
                  <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                    Add specific terms for this course that students must agree to before purchasing.
                  </p>
                </div>
                <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="rounded-full" 
                      
                      data-testid="edit-terms-button"
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      {course?.terms_and_conditions ? 'Edit Terms' : 'Add Terms'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="terms-dialog">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                        {course?.terms_and_conditions ? 'Edit' : 'Add'} Terms & Conditions
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFF8E7', border: '1px solid #E2A761' }}>
                        <p className="text-sm" style={{ color: '#5C635A' }}>
                          <strong>Note:</strong> These terms will be shown to students before they purchase this course. 
                          They must accept these terms to complete their purchase.
                        </p>
                      </div>
                      
                      <div>
                        <Label htmlFor="terms-content">Terms & Conditions</Label>
                        <Textarea
                          id="terms-content"
                          value={termsContent}
                          onChange={(e) => setTermsContent(e.target.value)}
                          placeholder="Enter your course-specific terms and conditions here...

Example:
1. Course content is for personal use only.
2. No refunds after 24 hours of purchase.
3. Content cannot be shared or redistributed.
4. Access is granted for 12 months from purchase date.
..."
                          className="min-h-[300px] mt-2"
                          style={{ fontFamily: 'inherit' }}
                          data-testid="terms-content-textarea"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          variant="outline"
                          onClick={() => setShowTermsDialog(false)}
                          data-testid="cancel-terms-button"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveTerms}
                          disabled={isSavingTerms}
                          className="rounded-full"
                          
                          data-testid="save-terms-button"
                        >
                          {isSavingTerms ? 'Saving...' : 'Save Terms'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Display current terms */}
              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                {course?.terms_and_conditions ? (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="h-5 w-5" style={{ color: '#163329' }} />
                      <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>Current Terms</h3>
                      {course?.terms_updated_at && (
                        <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>
                          Updated {new Date(course.terms_updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div 
                      className="prose prose-sm max-w-none p-4 rounded-lg whitespace-pre-wrap"
                      style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}
                      data-testid="current-terms-display"
                    >
                      {course.terms_and_conditions}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                    <p style={{ color: '#5C635A' }}>No terms and conditions set for this course yet.</p>
                    <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                      Click "Add Terms" above to create course-specific terms.
                    </p>
                  </div>
                )}
              </Card>

              {/* Acknowledgment Declaration Section */}
              <div className="pt-6 border-t" style={{ borderColor: '#E3E1DB' }}>
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      Acknowledgment Declaration
                    </h2>
                    <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                      Create a declaration that learners must acknowledge before enrolling. Requires admin approval.
                    </p>
                  </div>
                  <Dialog open={showDeclarationDialog} onOpenChange={setShowDeclarationDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        className="rounded-full" 
                        onClick={openDeclarationDialog}
                        data-testid="edit-declaration-button"
                      >
                        <Edit2 className="mr-2 h-4 w-4" />
                        {declarationStatus ? 'Edit Declaration' : 'Add Declaration'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="declaration-dialog">
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                          {declarationStatus ? 'Edit' : 'Create'} Acknowledgment Declaration
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#E8F5E9', border: '1px solid #4CAF50' }}>
                          <p className="text-sm" style={{ color: '#2E7D32' }}>
                            <strong>Note:</strong> This declaration will be shown to learners before they can purchase or enroll in this course. 
                            They must acknowledge and accept it to proceed. Use [Full Name] as a placeholder for the learner's name.
                          </p>
                        </div>
                        
                        {declarationStatus && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Status:</span>
                            <span 
                              className="text-xs px-2 py-1 rounded-full"
                              style={{ 
                                backgroundColor: declarationStatus === 'approved' ? '#E8F5E9' : declarationStatus === 'pending' ? '#FFF8E7' : '#FEE2E2',
                                color: declarationStatus === 'approved' ? '#2E7D32' : declarationStatus === 'pending' ? '#B45309' : '#DC2626'
                              }}
                            >
                              {declarationStatus.charAt(0).toUpperCase() + declarationStatus.slice(1)}
                            </span>
                          </div>
                        )}
                        
                        {isLoadingDeclaration ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#163329' }} />
                          </div>
                        ) : (
                          <div>
                            <Label htmlFor="declaration-content">Declaration Text</Label>
                            <Textarea
                              id="declaration-content"
                              value={declarationContent}
                              onChange={(e) => setDeclarationContent(e.target.value)}
                              placeholder="Enter your declaration text here...

Example:
I, [Full Name], hereby acknowledge and declare that:

1. I understand the course materials are for educational purposes only.
2. I will complete all assignments to the best of my ability.
3. I agree to maintain academic integrity throughout this course.
4. I understand the certification requirements and grading criteria.
..."
                              className="min-h-[300px] mt-2"
                              style={{ fontFamily: 'inherit' }}
                              data-testid="declaration-content-textarea"
                            />
                          </div>
                        )}
                        
                        <div className="flex justify-end gap-3 pt-4">
                          <Button
                            variant="outline"
                            onClick={() => setShowDeclarationDialog(false)}
                            data-testid="cancel-declaration-button"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSaveDeclaration}
                            disabled={isSavingDeclaration || !declarationContent.trim()}
                            className="rounded-full"
                            data-testid="save-declaration-button"
                          >
                            {isSavingDeclaration ? 'Saving...' : 'Submit for Approval'}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Display current declaration */}
                <Card className="p-6 mt-4" style={{ borderColor: '#E3E1DB' }}>
                  {declarationStatus ? (
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="h-5 w-5" style={{ color: '#163329' }} />
                        <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>Current Declaration</h3>
                        <span 
                          className="text-xs px-2 py-1 rounded-full"
                          style={{ 
                            backgroundColor: declarationStatus === 'approved' ? '#E8F5E9' : declarationStatus === 'pending' ? '#FFF8E7' : '#FEE2E2',
                            color: declarationStatus === 'approved' ? '#2E7D32' : declarationStatus === 'pending' ? '#B45309' : '#DC2626'
                          }}
                        >
                          {declarationStatus.charAt(0).toUpperCase() + declarationStatus.slice(1)}
                        </span>
                      </div>
                      <div 
                        className="prose prose-sm max-w-none p-4 rounded-lg whitespace-pre-wrap"
                        style={{ backgroundColor: '#F0EFEA', color: '#5C635A' }}
                        data-testid="current-declaration-display"
                      >
                        {declarationContent}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                      <p style={{ color: '#5C635A' }}>No acknowledgment declaration created yet.</p>
                      <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                        Click "Add Declaration" above to create a learner acknowledgment declaration.
                      </p>
                    </div>
                  )}
                </Card>
              </div>

              <ModelLetterEditor courseId={courseId} />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Course Settings
              </h2>

              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div 
                      className="h-12 w-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: course?.chat_enabled !== false ? '#E8F5E9' : '#FEE2E2' }}
                    >
                      <MessageCircle 
                        className="h-6 w-6" 
                        style={{ color: course?.chat_enabled !== false ? '#2E7D32' : '#DC2626' }} 
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold" style={{ color: '#163329' }}>
                        Private Chat with Students
                      </h3>
                      <p className="text-sm" style={{ color: '#5C635A' }}>
                        {course?.chat_enabled !== false 
                          ? 'Students enrolled in this course can send you private messages'
                          : 'Private messaging is disabled for this course'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium" style={{ color: '#5C635A' }}>
                      {course?.chat_enabled !== false ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={course?.chat_enabled !== false}
                      onCheckedChange={handleToggleChat}
                      disabled={isTogglingChat}
                      data-testid="chat-toggle"
                    />
                  </div>
                </div>
              </Card>

              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  <strong>Note:</strong> When chat is disabled, students will not be able to send new messages. 
                  Existing conversations will still be visible but students cannot reply.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Course Gallery
                </h2>
              </div>

              {/* Upload New Image Card */}
              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#163329' }}>
                  <Upload className="h-4 w-4" />
                  Add Gallery Image
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="gallery-title">Image Title (optional)</Label>
                      <Input 
                        id="gallery-title" 
                        value={galleryTitle}
                        onChange={(e) => setGalleryTitle(e.target.value)}
                        placeholder="Enter image title..."
                        data-testid="gallery-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gallery-description">Description (optional)</Label>
                      <Input 
                        id="gallery-description" 
                        value={galleryDescription}
                        onChange={(e) => setGalleryDescription(e.target.value)}
                        placeholder="Enter image description..."
                        data-testid="gallery-description-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <input
                      ref={galleryFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleGalleryUpload}
                      className="hidden"
                      id="gallery-upload"
                    />
                    <label htmlFor="gallery-upload">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isUploadingGallery}
                        onClick={() => galleryFileInputRef.current?.click()}
                        className="w-full md:w-auto"
                      >
                        {isUploadingGallery ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading... {galleryUploadProgress}%
                          </>
                        ) : (
                          <>
                            <Image className="h-4 w-4 mr-2" />
                            Upload Image
                          </>
                        )}
                      </Button>
                    </label>
                    {isUploadingGallery && (
                      <Progress value={galleryUploadProgress} className="mt-2 w-full md:w-64" />
                    )}
                  </div>
                  
                  <p className="text-xs" style={{ color: '#5C635A' }}>
                    Supported formats: JPEG, PNG, WebP, GIF. Max size: 10MB per image.
                  </p>
                </div>
              </Card>

              {/* Gallery Images Grid */}
              {galleryImages.length > 0 ? (
                <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                  <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#163329' }}>
                    <Image className="h-4 w-4" />
                    Gallery Images ({galleryImages.length})
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryImages.map((image) => (
                      <div 
                        key={image.id}
                        className="relative aspect-square rounded-lg overflow-hidden group"
                        style={{ border: '1px solid #E3E1DB' }}
                      >
                        <img
                          src={image.presigned_url || image.url}
                          alt={image.title || 'Gallery image'}
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Overlay with title/description on hover */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex flex-col justify-between p-2 opacity-0 group-hover:opacity-100">
                          <div>
                            {image.title && (
                              <p className="text-white text-sm font-medium truncate">{image.title}</p>
                            )}
                            {image.description && (
                              <p className="text-white text-xs truncate">{image.description}</p>
                            )}
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => window.open(image.presigned_url || image.url, '_blank')}
                              className="h-7 w-7 p-0"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteGalleryImage(image.id)}
                              className="h-7 w-7 p-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ) : (
                <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
                  <Image className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                  <p style={{ color: '#5C635A' }}>No gallery images yet</p>
                  <p className="text-sm" style={{ color: '#5C635A' }}>
                    Upload images above to create your course gallery
                  </p>
                </Card>
              )}
              
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  <strong>Tip:</strong> Gallery images are visible to all visitors on the course detail page. 
                  Use them to showcase course materials, student work, or behind-the-scenes photos.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Brochure Tab */}
          <TabsContent value="brochure">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  Course Brochure
                </h2>
              </div>

              {/* Brochure Text Section */}
              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#163329' }}>
                  <FileText className="h-4 w-4" />
                  Brochure Text
                </h3>
                
                <div className="space-y-4">
                  <Textarea 
                    value={editCourseForm.brochure_text || ''}
                    onChange={(e) => setEditCourseForm(prev => ({ ...prev, brochure_text: e.target.value }))}
                    rows={6}
                    placeholder="Enter course brochure text or promotional description..."
                    data-testid="brochure-text-input"
                    style={{ borderColor: '#E3E1DB' }}
                  />
                  <p className="text-xs" style={{ color: '#5C635A' }}>
                    This text will be displayed on the course brochure tab visible to visitors.
                  </p>
                </div>
              </Card>

              {/* Brochure PDF Files Section */}
              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#163329' }}>
                  <FileText className="h-4 w-4" />
                  Brochure PDF Files ({(editCourseForm.brochure_files || []).length}/5)
                </h3>
                
                {/* List of existing brochure files */}
                {(editCourseForm.brochure_files || []).length > 0 ? (
                  <div className="space-y-3 mb-4">
                    {(editCourseForm.brochure_files || []).map((file, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: '#F0EFEA', border: '1px solid #E3E1DB' }}
                        data-testid={`brochure-file-item-${index}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="h-5 w-5 flex-shrink-0" style={{ color: '#D86B47' }} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate" style={{ color: '#163329' }}>
                              {file.filename || `Brochure ${index + 1}`}
                            </p>
                            {file.size_bytes && (
                              <p className="text-xs" style={{ color: '#5C635A' }}>
                                {(file.size_bytes / 1024 / 1024).toFixed(2)} MB
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(file.presigned_url || file.url, '_blank')}
                            data-testid={`view-brochure-${index}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRemoveBrochure(index)}
                            data-testid={`remove-brochure-file-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-8 mb-4 rounded-lg" style={{ backgroundColor: '#F0EFEA', border: '1px solid #E3E1DB' }}>
                    <FileText className="h-12 w-12 mx-auto mb-2" style={{ color: '#5C635A' }} />
                    <p style={{ color: '#5C635A' }}>No brochure files uploaded yet</p>
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      Upload PDF files below to add to your course brochure
                    </p>
                  </div>
                )}

                {/* Upload button */}
                {(editCourseForm.brochure_files || []).length < 5 ? (
                  <div>
                    <input
                      ref={brochureFileInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handleBrochureUpload}
                      className="hidden"
                      id="brochure-tab-upload"
                    />
                    <label htmlFor="brochure-tab-upload">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isUploadingBrochure}
                        onClick={() => brochureFileInputRef.current?.click()}
                        className="w-full md:w-auto"
                        data-testid="upload-brochure-file-btn"
                      >
                        {isUploadingBrochure ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading... {brochureUploadProgress}%
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload PDF
                          </>
                        )}
                      </Button>
                    </label>
                    {isUploadingBrochure && (
                      <Progress value={brochureUploadProgress} className="mt-2 w-full md:w-64" />
                    )}
                    <p className="text-xs mt-2" style={{ color: '#5C635A' }}>
                      Supported format: PDF only. Max size: 20MB per file.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: '#5C635A' }}>
                    Maximum of 5 brochure files reached
                  </p>
                )}
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleEditCourse}
                  disabled={isEditingCourse}
                  data-testid="save-brochure-btn"
                >
                  {isEditingCourse ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Brochure Changes'
                  )}
                </Button>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  <strong>Tip:</strong> The brochure is visible to all visitors on the course detail page. 
                  Use it to provide detailed course information, syllabus, or promotional materials.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates">
            <div className="space-y-6">
              {/* Certificate Setup Section */}
              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  <Award className="inline h-5 w-5 mr-2" />
                  Certificate Setup
                </h3>
                <p className="text-sm mb-4" style={{ color: '#5C635A' }}>
                  Configure the certificate template and terms text. Both are required before you can unlock certificates for students.
                </p>
                
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Template Upload */}
                  <div>
                    <Label className="text-sm font-medium">Certificate Template (Image/PDF)</Label>
                    <div className="mt-2">
                      {certificateSetup?.certificate_template_url ? (
                        <div className="border rounded-lg p-4" style={{ borderColor: '#E3E1DB' }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-5 w-5 text-green-500" />
                              <span className="text-sm" style={{ color: '#163329' }}>Template uploaded</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => certTemplateInputRef.current?.click()}
                              disabled={isUploadingCertTemplate}
                            >
                              Replace
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: '#E3E1DB' }}
                          onClick={() => certTemplateInputRef.current?.click()}
                        >
                          {isUploadingCertTemplate ? (
                            <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: '#163329' }} />
                          ) : (
                            <>
                              <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: '#5C635A' }} />
                              <p className="text-sm" style={{ color: '#5C635A' }}>Click to upload template</p>
                            </>
                          )}
                        </div>
                      )}
                      <input
                        ref={certTemplateInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleCertTemplateUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                  
                  {/* Certificate Terms */}
                  <div>
                    <Label className="text-sm font-medium">Certificate Terms Text</Label>
                    <Textarea
                      value={certificateTerms}
                      onChange={(e) => setCertificateTerms(e.target.value)}
                      placeholder="Enter terms and conditions text that will appear on the certificate..."
                      className="mt-2 min-h-[100px]"
                    />
                    <Button
                      onClick={saveCertificateTerms}
                      disabled={isSavingCertSetup}
                      size="sm"
                      className="mt-2"
                    >
                      {isSavingCertSetup ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Save Terms
                    </Button>
                  </div>
                </div>
                
                <div className={`mt-4 p-3 rounded-lg ${certificateSetup?.is_complete ? 'bg-green-50' : 'bg-amber-50'}`}>
                  <p className={`text-sm ${certificateSetup?.is_complete ? 'text-green-700' : 'text-amber-700'}`}>
                    {certificateSetup?.is_complete 
                      ? '✓ Certificate setup is complete. You can now unlock certificates for students.'
                      : '⚠ Please upload a template or add terms text before unlocking certificates.'}
                  </p>
                </div>
              </Card>

              {/* Student Grades & Certificates */}
              <Card className="p-6" style={{ borderColor: '#E3E1DB' }}>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                      Student Grades & Certificates
                    </h3>
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      View student progress, quiz scores, and manage certificate unlocking
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchStudentGrades}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh
                  </Button>
                </div>

                {isLoadingGrades ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
                  </div>
                ) : studentGrades.length === 0 ? (
                  <div className="text-center py-8" style={{ color: '#5C635A' }}>
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No students enrolled yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {studentGrades.map((student) => (
                      <div 
                        key={student.student_id}
                        className="border rounded-lg p-4"
                        style={{ borderColor: '#E3E1DB' }}
                        data-testid={`grade-card-${student.student_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                          {/* Student Info */}
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div 
                              className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold overflow-hidden flex-shrink-0"
                              style={{ backgroundColor: '#163329', color: 'white' }}
                            >
                              {student.avatar_url ? (
                                <img src={student.avatar_url} alt={student.name} className="w-full h-full object-cover" />
                              ) : (
                                student.name?.charAt(0)?.toUpperCase() || 'S'
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold truncate" style={{ color: '#163329' }}>
                                {student.name} {student.surname}
                              </p>
                              <p className="text-xs truncate" style={{ color: '#5C635A' }}>
                                {student.email}
                              </p>
                              {student.student_number && (
                                <Badge variant="outline" className="text-xs mt-1">
                                  {student.student_number}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Progress & Scores */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
                            <div className="text-center">
                              <p className="text-xs" style={{ color: '#5C635A' }}>Lessons</p>
                              <p className="text-lg font-semibold" style={{ color: '#163329' }}>
                                {student.lessons_completed}/{student.total_lessons}
                              </p>
                              {student.all_lessons_completed && (
                                <Badge className="text-xs bg-green-100 text-green-700">Complete</Badge>
                              )}
                            </div>
                            <div className="text-center">
                              <p className="text-xs" style={{ color: '#5C635A' }}>Quiz Score</p>
                              <p className={`text-lg font-semibold ${student.quiz_score !== null ? (student.passed ? 'text-green-600' : 'text-red-600') : ''}`}>
                                {student.quiz_score !== null ? `${student.quiz_score}%` : 'N/A'}
                              </p>
                              {student.passed !== null && (
                                <Badge className={`text-xs ${student.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {student.passed ? 'Passed' : 'Not Passed'}
                                </Badge>
                              )}
                            </div>
                            <div className="text-center">
                              <p className="text-xs" style={{ color: '#5C635A' }}>Quizzes</p>
                              <p className="text-lg font-semibold" style={{ color: '#163329' }}>
                                {student.quizzes_taken}/{student.total_quizzes}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs" style={{ color: '#5C635A' }}>Certificate</p>
                              {student.certificate_unlocked ? (
                                <Badge className="text-xs bg-green-100 text-green-700">
                                  <Unlock className="h-3 w-3 mr-1" />
                                  Unlocked
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  Locked
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2 justify-end">
                            {student.certificate_unlocked ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => lockCertificate(student.student_id)}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Lock className="h-4 w-4 mr-1" />
                                Revoke
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => unlockCertificate(student.student_id)}
                                disabled={!certificateSetup?.is_complete}
                                title={!certificateSetup?.is_complete ? 'Complete certificate setup first' : 'Unlock certificate'}
                              >
                                <Unlock className="h-4 w-4 mr-1" />
                                Unlock
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setShowGradeCommentDialog(student.student_id);
                                setGradeComment(student.grade_comment || '');
                              }}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Comment
                            </Button>
                          </div>
                        </div>

                        {/* Grade Comment */}
                        {student.grade_comment && (
                          <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                            <p className="text-xs font-medium" style={{ color: '#5C635A' }}>Comment:</p>
                            <p className="text-sm" style={{ color: '#163329' }}>{student.grade_comment}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                <p className="text-sm" style={{ color: '#5C635A' }}>
                  <strong>Note:</strong> Students need to complete all lessons to see the completion banner.
                  Quiz pass threshold is 70%. You can unlock certificates for any student regardless of their quiz score.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Media Preview Modal - A4 Size */}
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent 
            className="overflow-hidden" 
            data-testid="media-preview-dialog"
            style={{ 
              // A4 Portrait: 210mm × 297mm (794px × 1123px at 96 DPI)
              width: selectedMedia?.video_url?.endsWith('.pdf') ? '850px' : 'min(1400px, 95vw)',
              maxWidth: '95vw',
              height: '95vh',
              maxHeight: '95vh',
              padding: '0',
              display: 'flex',
              flexDirection: 'column',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            {/* Header Box - Fixed position */}
            <div 
              className="p-4 rounded-t" 
              style={{ 
                backgroundColor: '#F0EFEA',
                flexShrink: 0,
                margin: '32px 32px 0 32px'
              }}
            >
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                {selectedMedia?.title}
              </DialogTitle>
            </div>
            
            {/* Content Area - Fixed height to prevent jumping */}
            <div 
              style={{ 
                flex: 1,
                overflow: 'auto',
                padding: '24px 32px',
                minHeight: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
              }}
            >
              {renderMediaPreview(selectedMedia)}
            </div>
            
            {/* Footer Box - Fixed position */}
            <div 
              className="p-4 rounded-b" 
              style={{ 
                backgroundColor: '#F0EFEA',
                flexShrink: 0,
                margin: '0 32px 32px 32px'
              }}
            >
              <p className="text-sm font-medium mb-2" style={{ color: '#163329' }}>Description:</p>
              <p className="text-sm" style={{ color: '#5C635A' }}>
                {selectedMedia?.description}
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quiz Creation Dialog */}
        <Dialog open={!!showQuizDialog} onOpenChange={(open) => !open && setShowQuizDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Create Quiz
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              <div>
                <Label htmlFor="quiz-title">Quiz Title</Label>
                <Input
                  id="quiz-title"
                  value={quizForm.title}
                  onChange={(e) => setQuizForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Chapter 1 Assessment"
                  className="mt-1"
                />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Questions</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addQuestion}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Question
                  </Button>
                </div>

                {quizForm.questions.map((q, qIndex) => (
                  <Card key={qIndex} className="p-4" style={{ borderColor: '#E3E1DB' }}>
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <Label className="text-sm font-medium">Question {qIndex + 1}</Label>
                        {quizForm.questions.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeQuestion(qIndex)}
                            className="text-red-500 hover:text-red-600 h-6 w-6 p-0"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      <Textarea
                        value={q.question}
                        onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                        placeholder="Enter your question..."
                        rows={2}
                      />

                      <div className="space-y-2">
                        <Label className="text-xs">Options (select the correct answer)</Label>
                        {q.options.map((option, oIndex) => (
                          <div key={oIndex} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`correct-${qIndex}`}
                              checked={q.correct_answer === oIndex}
                              onChange={() => updateQuestion(qIndex, 'correct_answer', oIndex)}
                              className="h-4 w-4"
                              style={{ accentColor: '#163329' }}
                            />
                            <Input
                              value={option}
                              onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                              placeholder={`Option ${oIndex + 1}`}
                              className="flex-1"
                            />
                            {q.correct_answer === oIndex && (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowQuizDialog(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveQuiz}
                  disabled={isSavingQuiz}
                  className="flex-1"
                  
                >
                  {isSavingQuiz ? 'Saving...' : 'Create Quiz'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Lesson Dialog */}
        <Dialog open={!!showEditLesson} onOpenChange={(open) => !open && setShowEditLesson(null)}>
          <DialogContent className="max-w-lg" data-testid="edit-lesson-modal">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                Edit Lesson
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="edit-lesson-title">Lesson Title</Label>
                <Input 
                  id="edit-lesson-title"
                  value={editLessonForm.title}
                  onChange={(e) => setEditLessonForm(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="edit-lesson-title-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-lesson-description">Description</Label>
                <Textarea 
                  id="edit-lesson-description"
                  value={editLessonForm.description}
                  onChange={(e) => setEditLessonForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="edit-lesson-description-input"
                />
              </div>
              <div>
                <Label>Content Type</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      checked={editLessonVideoType === 'youtube'}
                      onChange={() => setEditLessonVideoType('youtube')}
                      className="h-4 w-4"
                      style={{ accentColor: '#163329' }}
                    />
                    <span>YouTube URL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      checked={editLessonVideoType === 'uploaded'}
                      onChange={() => setEditLessonVideoType('uploaded')}
                      className="h-4 w-4"
                      style={{ accentColor: '#163329' }}
                    />
                    <span>Upload File</span>
                  </label>
                </div>
              </div>
              <div>
                <Label htmlFor="edit-lesson-video-url">
                  {editLessonVideoType === 'youtube' ? 'YouTube Video URL' : 'File Path'}
                </Label>
                <Input 
                  id="edit-lesson-video-url"
                  value={editLessonForm.video_url}
                  onChange={(e) => setEditLessonForm(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder={editLessonVideoType === 'youtube' ? 'https://youtube.com/watch?v=...' : 'Upload file below to replace'}
                  readOnly={editLessonVideoType === 'uploaded'}
                  data-testid="edit-lesson-video-url-input"
                />
                {editLessonVideoType === 'uploaded' && (
                  <div className="mt-2">
                    <Label className="text-sm" style={{ color: '#5C635A' }}>Replace with new Video or PDF:</Label>
                    
                    <input 
                      type="file" 
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/mpeg,video/webm,.mp4,.mov,.avi,.mpeg,.webm,application/pdf,.pdf" 
                      onChange={handleEditLessonFileUpload} 
                      className="text-sm w-full mt-1 p-2 border rounded"
                      style={{ borderColor: '#E3E1DB' }}
                      data-testid="edit-lesson-file-upload"
                      disabled={isUploading || uploadStats.isProcessing}
                    />
                    
                    {/* Enhanced Upload Progress UI for Edit */}
                    {(isUploading || uploadStats.isProcessing) && (
                      <div className="mt-3 space-y-3 p-4 rounded-lg" style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}>
                        <div className="flex justify-between items-center">
                          <span style={{ color: '#163329' }} className="font-medium">
                            {uploadStats.isProcessing ? 'Processing Video' : 'Uploading...'}
                          </span>
                          <span style={{ color: '#D86B47' }} className="font-bold text-lg">
                            {uploadStats.percentage}%
                          </span>
                        </div>
                        <Progress 
                          value={uploadStats.percentage} 
                          className="h-3"
                          style={{ backgroundColor: '#E3E1DB' }}
                        />
                        <div className="flex justify-between text-xs" style={{ color: '#5C635A' }}>
                          <span>
                            {(uploadStats.bytesUploaded / (1024 * 1024)).toFixed(1)} MB / {(uploadStats.bytesTotal / (1024 * 1024)).toFixed(1)} MB
                          </span>
                          {uploadStats.estimatedSecondsRemaining !== null && !uploadStats.isProcessing && (
                            <span className="font-medium" style={{ color: '#163329' }}>
                              {formatTimeRemaining(uploadStats.estimatedSecondsRemaining)}
                            </span>
                          )}
                        </div>
                        {uploadStats.isProcessing && (
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#D86B47' }} />
                            <span className="text-sm" style={{ color: '#5C635A' }}>
                              {uploadStats.processingMessage || 'Cloudflare is transcoding your video...'}
                            </span>
                          </div>
                        )}
                        {/* Cancel Upload Button */}
                        {!uploadStats.isProcessing && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={handleCancelMainUpload}
                            className="w-full mt-2"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel Upload
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="edit-lesson-duration">Duration (minutes)</Label>
                <Input 
                  id="edit-lesson-duration"
                  type="number"
                  value={editLessonForm.duration}
                  onChange={(e) => setEditLessonForm(prev => ({ ...prev, duration: e.target.value }))}
                  data-testid="edit-lesson-duration-input"
                />
              </div>
              
              {/* Individual Purchase Settings */}
              <div className="border-t pt-4 mt-4" style={{ borderColor: '#E3E1DB' }}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label htmlFor="edit-is_purchasable" className="text-sm font-medium">Available for individual purchase</Label>
                    <p className="text-xs" style={{ color: '#5C635A' }}>Allow students to buy this lesson separately</p>
                  </div>
                  <input
                    type="checkbox"
                    id="edit-is_purchasable"
                    checked={editLessonForm.is_purchasable}
                    onChange={(e) => setEditLessonForm(prev => ({ ...prev, is_purchasable: e.target.checked }))}
                    className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    data-testid="edit-lesson-purchasable-toggle"
                  />
                </div>
                {editLessonForm.is_purchasable && (
                  <div>
                    <Label htmlFor="edit-lesson-price">Lesson Price (USD)</Label>
                    <Input 
                      id="edit-lesson-price" 
                      type="number" 
                      min="0" 
                      step="0.01"
                      value={editLessonForm.price}
                      onChange={(e) => setEditLessonForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0.00"
                      data-testid="edit-lesson-price-input"
                    />
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEditLesson(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditLesson}
                  disabled={isEditingLesson || isUploading}
                  className="flex-1"
                  
                  data-testid="save-lesson-button"
                >
                  {isEditingLesson ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rename File Dialog */}
        <Dialog open={!!showRenameDialog} onOpenChange={(open) => !open && setShowRenameDialog(null)}>
          <DialogContent className="sm:max-w-md top-4 translate-y-0 sm:top-[50%] sm:-translate-y-[50%]">
            <DialogHeader>
              <DialogTitle style={{ color: '#163329', fontFamily: 'Spectral, serif' }}>Rename File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="new-file-name">New File Name</Label>
                <Input
                  id="new-file-name"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                  placeholder="Enter new file name"
                  className="mt-1"
                  data-testid="rename-file-input"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowRenameDialog(null)}
                  disabled={isRenaming}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRenameFile}
                  disabled={isRenaming || !newFileName.trim()}
                  style={{ backgroundColor: '#163329' }}
                  data-testid="confirm-rename-button"
                >
                  {isRenaming ? 'Renaming...' : 'Rename'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move Lesson Dialog */}
        <Dialog open={!!showMoveLessonDialog} onOpenChange={(open) => !open && setShowMoveLessonDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                <FolderInput className="h-5 w-5 inline mr-2" style={{ color: '#0288D1' }} />
                Move Lesson to Another Course
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}>
                <p className="text-sm" style={{ color: '#5C635A' }}>Moving lesson:</p>
                <p className="font-medium" style={{ color: '#163329' }}>{showMoveLessonDialog?.title}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Select destination course</Label>
                {teacherCourses.length === 0 ? (
                  <p className="text-sm py-4 text-center" style={{ color: '#5C635A' }}>
                    No other courses available. Create a new course first, then move the lesson.
                  </p>
                ) : (
                  <div className="max-h-60 overflow-y-auto space-y-2 border rounded-md p-2" style={{ borderColor: '#E3E1DB' }}>
                    {teacherCourses.map((course) => (
                      <div
                        key={course.id}
                        onClick={() => setSelectedDestinationCourse(course.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedDestinationCourse === course.id 
                            ? 'ring-2 ring-blue-500' 
                            : 'hover:bg-gray-50'
                        }`}
                        style={{ 
                          backgroundColor: selectedDestinationCourse === course.id ? '#E1F5FE' : '#F5F5F0',
                          border: `1px solid ${selectedDestinationCourse === course.id ? '#29B6F6' : '#E3E1DB'}`
                        }}
                      >
                        <p className="font-medium text-sm" style={{ color: '#163329' }}>{course.title}</p>
                        <p className="text-xs" style={{ color: '#5C635A' }}>
                          {course.lesson_count || 0} lessons
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowMoveLessonDialog(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMoveLessonToCourse}
                  disabled={!selectedDestinationCourse || isMoving}
                  className="flex-1"
                  style={{ backgroundColor: '#0288D1' }}
                >
                  {isMoving ? 'Moving...' : 'Move Lesson'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move File Dialog */}
        <Dialog open={!!showMoveFileDialog} onOpenChange={(open) => !open && setShowMoveFileDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>
                <FolderInput className="h-5 w-5 inline mr-2" style={{ color: '#0288D1' }} />
                Move File to Another Lesson
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#F5F5F0', border: '1px solid #E3E1DB' }}>
                <p className="text-sm" style={{ color: '#5C635A' }}>Moving file:</p>
                <p className="font-medium" style={{ color: '#163329' }}>{showMoveFileDialog?.file?.file_name}</p>
              </div>
              
              {/* Step 1: Select destination course */}
              <div className="space-y-2">
                <Label>1. Select destination course</Label>
                {teacherCourses.length === 0 ? (
                  <p className="text-sm py-2 text-center" style={{ color: '#5C635A' }}>
                    No other courses available.
                  </p>
                ) : (
                  <select
                    value={selectedDestinationCourse || ''}
                    onChange={(e) => handleDestinationCourseSelect(e.target.value)}
                    className="w-full p-2 border rounded-md text-sm"
                    style={{ borderColor: '#E3E1DB' }}
                  >
                    <option value="">-- Select a course --</option>
                    <option value={courseId}>{course?.title} (Current course)</option>
                    {teacherCourses.map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Step 2: Select destination lesson */}
              {selectedDestinationCourse && (
                <div className="space-y-2">
                  <Label>2. Select destination lesson</Label>
                  {destinationCourseLessons.length === 0 ? (
                    <p className="text-sm py-2 text-center" style={{ color: '#5C635A' }}>
                      {selectedDestinationCourse === courseId 
                        ? 'Select a different lesson in this course.'
                        : 'This course has no lessons. Create a lesson first.'}
                    </p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-2" style={{ borderColor: '#E3E1DB' }}>
                      {destinationCourseLessons
                        .filter(l => l.id !== showMoveFileDialog?.lessonId) // Exclude current lesson
                        .map((lesson) => (
                          <div
                            key={lesson.id}
                            onClick={() => setSelectedDestinationLesson(lesson.id)}
                            className={`p-2 rounded cursor-pointer transition-colors ${
                              selectedDestinationLesson === lesson.id 
                                ? 'ring-2 ring-blue-500' 
                                : 'hover:bg-gray-50'
                            }`}
                            style={{ 
                              backgroundColor: selectedDestinationLesson === lesson.id ? '#E1F5FE' : '#F5F5F0',
                              border: `1px solid ${selectedDestinationLesson === lesson.id ? '#29B6F6' : '#E3E1DB'}`
                            }}
                          >
                            <p className="font-medium text-sm" style={{ color: '#163329' }}>{lesson.title}</p>
                            <p className="text-xs" style={{ color: '#5C635A' }}>
                              {lesson.files?.length || 0} files
                            </p>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowMoveFileDialog(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleMoveFile}
                  disabled={!selectedDestinationLesson || isMoving}
                  className="flex-1"
                  style={{ backgroundColor: '#0288D1' }}
                >
                  {isMoving ? 'Moving...' : 'Move File'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Floating scroll buttons */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col gap-2">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all"
          style={{ backgroundColor: 'rgba(22, 51, 41, 0.8)', color: '#F5F5F0' }}
          title="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
        <button
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          className="w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all"
          style={{ backgroundColor: 'rgba(22, 51, 41, 0.8)', color: '#F5F5F0' }}
          title="Scroll to bottom"
        >
          <ArrowDown className="h-5 w-5" />
        </button>
      </div>

      {/* Grade Comment Dialog */}
      <Dialog open={!!showGradeCommentDialog} onOpenChange={(open) => !open && setShowGradeCommentDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Add Grade Comment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={gradeComment}
              onChange={(e) => setGradeComment(e.target.value)}
              placeholder="Enter your comment about this student's performance..."
              rows={4}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowGradeCommentDialog(null)}>
                Cancel
              </Button>
              <Button onClick={saveGradeComment} disabled={isSavingComment}>
                {isSavingComment ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save Comment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseManagePage;